import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {GridApi, IDatasource, IRowNode} from "ag-grid-community";
import {fixEmptyFullWidthRowGroup} from "src/app/common/utils/ag-grid-a11y";
import {format} from "date-fns";
import {PlanDetailHeaderRowComponent} from "../plan-detail-header-row/plan-detail-header-row.component";
import {
  PlanDetailClickableHeaderComponent
} from "../plan-detail-clickable-header/plan-detail-clickable-header.component";
import {BasePage} from "../../base-page";
import {TranslateService} from "@ngx-translate/core";
import {Store} from "@ngrx/store";
import {saveAs} from 'file-saver';
import {Actions, ofType} from "@ngrx/effects";
import {Observable, Subscription} from "rxjs";
import {
  AppActionTypes,
  ApplyFTERequired,
  ApplyMuParams, ApplyNewHireClassesSuccess,
  EditPlanDetail,
  EditPlanDetailReceived,
  ExportEveryCt,
  ExportEveryCtFailed,
  ExportEveryCtSuccess,
  GetPlanDetail, GetPlanDetailV2,
  LoadPlanDetail, LoadPlanDetailV2,
  MultiEditPlanDetail, MultiEditPlanDetailV2,
  PlanDetailReceived,
  PlanDetailReceivedV2,
  PlanNotExist,
  SavePlanDetail,
  SavePlanSettings
} from "src/app/store/actions";
import {PlanLoadingOverlayComponent} from "../plan-loading-overlay/plan-loading-overlay.component";
import {DatePipe, TitleCasePipe} from "@angular/common";
import {NumericEditorComponent} from "../common/cell-editors/numeric/numeric-editor.component";
import {
  CachedMU,
  EditPlanDetailRequestDTO,
  EntityType,
  Features,
  LRUCache,
  MultiEditPlanDetailRequestDTO, MultiEditPlanDetailRequestDTOV2,
  PlanDetailResponse, PlanDetailResponseV2, SelectedMU
} from "src/app/models/plan";
import {ValidatorFactory} from "../common/validator-factory";
import {PlanCellType,  PlanParam} from "../common/plan-param";

import {MessageService} from "primeng/api";
import {NgbDropdownConfig, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {WfmModalComponent as modalComponent} from "src/app/components/wfm-modal/wfm-modal.component";
import {ConfirmationMsgFactory} from "../common/confirm-msg-factory";
import {DialogOptions} from "../../../common/dialog-options";
import {ConvertPlanDialogComponent} from "../convert-plan-dialog/convert-plan-dialog.component";
import {PlanSaveAsComponent} from "../plan-save-as/plan-save-as.component";
import {AuthenticationService} from "../../../authentication.service";
import {PlanHelper} from "../../../helpers/plan-helper";
import {ColumnId} from "../plan-list-page/plan-list-page.component";
import {filter, take} from "rxjs/operators";
import {BackgroundTask} from "../../../store/application-state";
import {WfmSelectComponent} from "src/app/components/wfm-select/wfm-select.component";
import {PlanSettingsDialogComponent} from "../plan-settings-dialog/plan-settings-dialog.component";
import {CTDistributionCalcMethod, PlanContactTypes} from "../../../common/enums";
import {NumberUtils} from "../../../common/utils/number.utils";
import {announceToScreenReader, setGridNavigationRole, suppressRowHeaderEditButtonKeyboardEvent} from "../../../common/utils/ag-grid-a11y";
import {associateMultiselectVisibleLabel, enforceMultiselectSingleSelectAria, sanitizeMultiselectComboboxValue} from "../../../common/utils/multiselect-a11y";
import clipboardCopy from 'clipboard-copy';
import {environment} from "../../../../environments/environment";
import {ModelUpdatedEvent} from "ag-grid-community";
import {DateUtils} from "../../../common/utils/date";
import {MswfRulesDialogComponent} from "../mswf-rules-dialog/mswf-rules-dialog.component";
import {ClipboardModule} from '@ag-grid-enterprise/clipboard';
import {RangeSelectionModule} from '@ag-grid-enterprise/range-selection';
import {RowGroupingModule} from '@ag-grid-enterprise/row-grouping';
import {EnterpriseCoreModule} from '@ag-grid-enterprise/core';
import {PlanDetailValidationCellComponent} from '../plan-detail-validation-cell/plan-detail-validation-cell.component';
import {ExcelExportModule} from '@ag-grid-enterprise/excel-export';

import {DebounceUtil} from '../../../helpers/debounce';
import {MultiSelect} from 'primeng/multiselect';
import {EspNoRowsOverlayComponent} from '../../../components/no-rows-overlay/no-rows-overlay.component';
import {MuGroupHeaderComponentV2} from '../../../components/mu-group-header-v2/mu-group-header-v2.component';
import {NewHireDialogV2Component} from '../new-hire-dialog-v2/new-hire-dialog-v2.component';
import {PlanSettingsCtDialogV2Component} from '../plan-settings-ct-dialog-v2/plan-settings-ct-dialog-v2.component';
import { TenantService } from "../../../tenant.service";
import { associateGridRowHeaders, clearGridSelectionOnBlur, fixGridNonActiveTabOrder, makeGridScrollRegionFocusable, patchMuGroupRowToggleAria, setRollupCollapsedAriaHidden } from "../../../common/utils/ag-grid-a11y";
import {DetailViewMode} from "./detail-view-mode.enum";
import { ExcelService, ImportedCellRef } from '../../../services/excel.service';

// Re-exported for backward compatibility so existing consumers can keep
// importing DetailViewMode from this component module. The enum lives in its
// own module to avoid a circular dependency with plan-detail-clickable-header.
export {DetailViewMode};
export enum ParamPermission {
    Modify = "MODIFY",
    ReadOnly = "READ"
}

@Component({
  standalone: false,
  selector: "plan-detail-v5",
  templateUrl: "./plan-detail-v5.component.html",
  styleUrls: ["./plan-detail-v5.component.scss"],
  providers: [MessageService, ExcelService, NgbDropdownConfig, TitleCasePipe]
})
export class PlanDetailComponentV5 extends BasePage implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('ctSelector') ctSelector: MultiSelect;
  @ViewChild('muSelector') muSelector: MultiSelect;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  private selectorAriaObserver: MutationObserver | null = null;

  private comboboxA11yObserver: MutationObserver | null = null;
  private comboboxLabelObserver: MutationObserver | null = null;
  private ctSingleSelectAriaObserver: MutationObserver | null = null;

  public dateLocale = DateUtils.getDatefnsLocale("en");
  public readonly ERROR_TOAST_LIFE = 4000;
  private pinnedRightColumnWidth = 105;
  private pinnedRightTotalColumnWidth = 115;
  private pinnedLeftColumnWidth = 225;
  private dateColumnWidth = 110;
  private weeklyDateColumnWidth = 140; // Wider columns for weekly view
  private dateShortFormat = "M/d";

  /**
   * Get the appropriate column width based on the current view mode
   * Weekly intervals get wider columns for better readability in Excel exports
   */
  private getDateColumnWidth(): number {
    return this.gridContext.viewMode === DetailViewMode.Weekly ? this.weeklyDateColumnWidth : this.dateColumnWidth;
  }
  public  _isLoading = true;
  public _dataIsSaved = true;
  private _userHasModify = false;

  public planId: string = null;
  public gridApi: GridApi = null;
  private fullWidthRowGroupObserver: { disconnect(): void } | null = null;
  private rowHeaderObserver: { disconnect(): void } | null = null;
  private muGroupToggleObserver: { disconnect(): void } | null = null;
  private scrollRegionSyncCleanup: (() => void) | null = null;
  private clearSelectionOnBlur: (() => void) | null = null;
  public gridParams: any;
  protected subscriptionList: Array<Subscription> = [];
  public datasource: IDatasource;
  public frameworkComponents: any;
  public isChartView=false;
  public chartsCount=0;
  public planName: String;
  public planStartDate: String;
  public planEndDate: String;
  public contactTypeList: any = [];
  private managementUnitsList: any = [];
  public selectedContactType:any = null;

  public static readonly ALL_CONTACT_TYPES:string = "ALL_CONTACT_TYPES";
  public static readonly MANAGEMENT_UNITS:string = "MANAGEMENT_UNITS";
  public planEntityType:any = null;
  public entityId: any;
  public entityName: String;
  public ltForecastName: String;
  public task:BackgroundTask = null;//{progress:55,status:TaskState.InProgress};
  public isPlanLoaded:boolean;
  public _isFTERequiredDirty = false;
  public suppressLoadingOverlay = true; //we don't want to display spinner when plan is loading
  protected translationKeys = ["plan.label.param", "plan.label.total", "plan.label.average", "date.format.short"];
  @ViewChild("grid")
  private grid;
  public isRenderingData = false;
  @ViewChild("muctSelector") muctSelector: WfmSelectComponent;

  colDefs = [];
  colDefsOld = [];
  excelStyles: any[] = [];

  data = null;
  rowData = null;
  topRowsData = null;
  allRowsData = null;

  rowDataDaily = [];

  rowDataMonthly = [];

  rowDataWeekly = [];

  private weekStartDate = 0;
  public language = "en";
  public locale = "en-US";
  public username = null;
  public userInfo$: Observable<string>;
  public loadingOverlayComponent = "planLoadingOverlay";
  protected _isNestedRoute:boolean;
  public _newHireSettingsFeature:boolean = false;
  public _externalForecastFeature:boolean = false;
  public _planSyncFeature:boolean = false;

  private _isMUDirty:boolean = false;
  private columnApi: any = null;
  private planSettings: any = {};
  private localParams : any = PlanParam;
  private planContactTypes: PlanContactTypes = PlanContactTypes.NA;
  private scrollTargetColumn: any;
  private scrollTargetLeft: any;
  public multiStepWorkflow: any;
  private modules = [ClipboardModule, RangeSelectionModule, EnterpriseCoreModule, RowGroupingModule, ExcelExportModule]
  private gridOptions: any;

  private editingContext: any;
  private lastEditedCell: any;
  public _dirtyCells = new Set();
  public _invalidCells = new Set();
  private _isGridValid: boolean = true;
  public _isRollupCollapsed = false;
  private _isPristine: boolean;
  private rowClassRules = null;
  private cellClassRules = null;
  private groupRowRendererParams: any;
  // Full group-row renderer (replaces AG Grid's default group renderer) so MU
  // group rows render flat as `div > mu-group-header-v2` with our accessible
  // button for ALL rows, matching the Plan rollup full-width row instead of
  // nesting inside AG Grid's group-cell wrapper (which adds a second native
  // caret).
  groupRowRenderer: any = MuGroupHeaderComponentV2;
  defaultColDef = null;
  private selectedCTs: any = [];
  public selectedMUs: any = [];
  public isManualEditing: boolean = false;
  public currentEntityView: MUCTKey = MUCTKey.CT
  private shouldRefreshMUs: boolean = false;
  private entitySettings: any;
  public cacheSelectedMUs: any = [];

  public readonly cachedMUsKey = "selectedMUs";

  public get isCTView(){
    return this.currentEntityView === MUCTKey.CT
  }
  public get isMUView(){
    return this.currentEntityView === MUCTKey.MU
  }
  public get isGridInvalid(): boolean {
    return !this._isGridValid;
  }
  get disableEntityViewToggle(){
    return this._isMUDirty || this._isFTERequiredDirty
  }

  get hasRows(){
    return this.rowData?.length>0;
  }
  constructor(
    private translateSrv: TranslateService,
    private store: Store<any>,
    private messageService: MessageService,
    private action$: Actions,
    private route: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    public modalService: NgbModal,
    private ngbDropdownConfig: NgbDropdownConfig,
    private titleCasePipe: TitleCasePipe,
    private authService: AuthenticationService,
    public tenantService: TenantService,
    private el: ElementRef,
    private excelService: ExcelService) {

    super(translateSrv);

    this._newHireSettingsFeature = authService.hasFeature(Features.NEW_HIRE_SETTINGS);
    this._externalForecastFeature = authService.hasFeature(Features.EXTERNAL_FORECAST);
    this._planSyncFeature = authService.hasFeature(Features.PLAN_SYNC);

    this.groupRowRendererParams = {
      suppressCount:true
    }
    ngbDropdownConfig.placement = "bottom-right";

    this.updateGridValid = DebounceUtil.debounce(this.updateGridValid.bind(this),100);

    this.userInfo$ = this.store.select("state");
    this.userInfo$.subscribe((value: any) => {
      if(!value) return;
      this.setUsernameLocale(value);

    });

  }
  public setUsernameLocale(value: any) {
    if (value.user) this.username = value.user.username;
    if (value.locale) this.locale = value.locale;
    if (value.user && value.user.language) {
      if (this.language != value.user.language) {
        this.dateLocale = DateUtils.getDatefnsLocale(value.user.language);
        this.language = value.user.language;
      }
    }
  }

  get UnappliedChanges(){
    return this._isLoading || this._isMUDirty || this._isFTERequiredDirty || this._isGridValid==false
  }

  isDisableExportAsCsv():boolean {
    return (this.planSettings && this.planSettings.ctDistributionCalcMethod == CTDistributionCalcMethod.FTE_REQUIRED)
      && this._isFTERequiredDirty;
  }

  isDisableImportExcel(): boolean {
    if (!this._userHasModify) return true;
    if (this.currentEntityView === MUCTKey.MU) {
      return !this.isMUApplyDisabled;
    }
    return this.UnappliedChanges;
  }

  isDisableExportExcel(): boolean {
    if (this.currentEntityView === MUCTKey.MU) {
      return !this.isMUApplyDisabled;
    }
    return this.isDisableExportAsCsv();
  }

  isDisableExportEveryCt():boolean {
    let isDisable = false;

    if (this.currentEntityView == MUCTKey.MU) {
      isDisable = !this.isMUApplyDisabled;
    } else {
      isDisable = this.isDisableExportAsCsv();
    }

    return isDisable;
  }

  async showConvertToActiveDialog(){

    if(this._dataIsSaved) {
      try {
        const modalRef = this.modalService.open(ConvertPlanDialogComponent, {
          centered: true,
          windowClass: "b-info wfm-modal",
          container: "div.plan-detail-container",
          backdrop: "static",
          keyboard: false
        });

        modalRef.componentInstance.planStartDate = this.planStartDate;
        modalRef.componentInstance.planEndDate = this.planEndDate;
        modalRef.componentInstance.planName = this.planName;
        modalRef.componentInstance.planId = this.planId;
        await modalRef.result;
      }catch(e){

      }
    }else{
      let dgOption: DialogOptions = new DialogOptions();
      dgOption.titleKey = "plan.convert.unsaved.dialog.title";
      dgOption.messageKey = "plan.convert.unsaved.dialog.msg";
      dgOption.msgType = "warn";
      dgOption.showCancel = true;
      dgOption.confirmLabel = "btn.confirm.label";
      try{
        let result = await modalComponent.showModalMessage(dgOption, this.modalService);
        this.action$.pipe(
          ofType(AppActionTypes.SavePlanDetailComplete),
          take(1))
          .subscribe((action:any)=>{
            if(action.payload !== null){
              this.showConvertToActiveDialog();
            }

          });
        this.savePlan();
      }catch(e){
        //user canceled
      }


    }
  }


  async showMswfRulesDlg() {
    try {
      const modalRef = this.modalService.open(MswfRulesDialogComponent, {
        centered: true,
        windowClass: "b-info wfm-modal mswf-rules",
        container: "div.plan-detail-container",
        backdrop: "static",
        keyboard: false
      });
      modalRef.componentInstance.mswfData = this.multiStepWorkflow;
      modalRef.componentInstance.planName = this.planName;
      modalRef.componentInstance.displayPlanName = false;


      await modalRef.result;
    } catch (e) {
      console.log(e);
    }
  }

  get isMUgrid(){
    return this.currentEntityView==MUCTKey.MU;
  }

  get isMUApplyDisabled(){
    return this._isLoading || !this._isMUDirty;
  }

  get isWorkloadCTgrid(){
    return this.currentEntityView==MUCTKey.CT
          && this.planSettings?.planContactTypes === PlanContactTypes.WORKLOAD;
  }

  get CTDistMode():CTDistributionCalcMethod{
    if(this.planSettings) {
      return this.planSettings.ctDistributionCalcMethod;
    }else{
      return null;
    }
  }
  setMUGridDirty(dirty:boolean){
    this._isMUDirty = dirty;
    const ctl = [...this.contactTypeList];
    if(dirty){
      //disable ct selections
      for (let index = 0; index < ctl.length; index++) {
        const e = ctl[index];
        if(e.type != MUCTKey.MU){
          e.disabled = true;
        }
      }
    }
    else{
      //enable ct selections
      for (let index = 0; index < ctl.length; index++) {
        const e = ctl[index];
        e.disabled = false;
      }
    }
    this.contactTypeList = ctl;

  }

  applyMUEdits(){
    console.debug("applying MU edits");
    this._isLoading = true;
    //this.gridApi.showLoadingOverlay();
    this.store.dispatch(new ApplyMuParams({planId: this.planId}));
  }

  applyFTERequired(){
    console.debug("applying FTE required");
    this.store.dispatch(new ApplyFTERequired({planId: this.planId}));
  }

  gatherCurrentSettings(): any {
    return {
      ctDistributionCalcMethod: this.planSettings.ctDistributionCalcMethod,

      usePatienceTime: this.planSettings.medianPatienceTime == null ? false : true,
      medianPatienceTime: this.planSettings.medianPatienceTime == null ? null : String(this.planSettings.medianPatienceTime),
      planSettingsWorkloadType: this.planSettings.planSettingsWorkloadType == null ? PlanContactTypes.NA : this.planSettings.planSettingsWorkloadType
    }
  }

  showPlanSettingsDialog() {
    this._showPlanSettingsDialog(this.gatherCurrentSettings(), false);
  }

  _showPlanSettingsDialog(settings, isDirty) {
    const modalRef = this.modalService.open(PlanSettingsDialogComponent, {
      centered: true,
      windowClass: "b-info wfm-modal",
      container: "div.plan-detail-container",
      backdrop: "static",
      keyboard: false
    });

    modalRef.componentInstance.reset(settings, isDirty, this.gatherCurrentSettings());

    modalRef.result.then((data) => {
      this.store.dispatch(new SavePlanSettings({planId: this.planId,planSettings:data}));
    }, (reason) => {

    });
  }



  showNewHireSettingsDialog(mu) {
    const modalRef = this.modalService.open(NewHireDialogV2Component, {
      centered: true,
      windowClass: "b-info wfm-modal",
      container: "div.plan-detail-container",
      backdrop: "static",
      keyboard: false
    });

    modalRef.componentInstance.muSettings = this.entitySettings[mu];
    modalRef.componentInstance.muOid = mu;
    modalRef.componentInstance.planVersion = 2;
    if (this._newHireSettingsFeature) {
      modalRef.componentInstance.canEdit = false;
    }

    modalRef.result.then((newHireTrainingWeeks) => {
      this._dataIsSaved = false;
      this.entitySettings[mu] = {...this.entitySettings[mu],newHireTrainingWeeks}
    }, (reason) => {

    });
  }

  showCtSettingsDialog() {
    const modalRef = this.modalService.open(PlanSettingsCtDialogV2Component, {
      centered: true,
      windowClass: "b-info wfm-modal",
      container: "div.plan-detail-container",
      backdrop: "static",
      keyboard: false
    });

    let entityOid = this.getSelectedEntityOid();
    modalRef.componentInstance.planId = this.planId;
    modalRef.componentInstance.interval = this.gridContext.viewMode
    modalRef.componentInstance.entitydOid = entityOid;
    modalRef.componentInstance.entitySettings = this.entitySettings[entityOid];

    modalRef.result.then((data) => {
      this._dataIsSaved = false;
      this.entitySettings[entityOid] = {...this.entitySettings[entityOid],startingBacklog:data};
    }, (reason) => {

    });
  }

  async showSaveAsDlg() {
    try {
      const modalRef = this.modalService.open(PlanSaveAsComponent, {
        centered: true,
        windowClass: "b-info wfm-modal",
        container: "div.plan-detail-container",
        backdrop: "static",
        keyboard: false
      });
      let component = modalRef.componentInstance;
      component.planId = this.planId;
      component.entityType = this.planEntityType;
      component.entityId = this.entityId;
      component.entityName = this.entityName;
      component.ltfcstName = this.ltForecastName;
      component.planStartDate = this.planStartDate;
      component.planEndDate = this.planEndDate;

      await modalRef.result;
    } catch(error) {
      if (error) {
        console.log("error during showSaveAsDlg: " + error.message);
      }
    }
  }

  public gridContext: any = {
    viewMode: DetailViewMode.Monthly,
  };

  //commented due to popover flickering
  // get gridContext() {
  //   return {updateViewMode: this.updateViewMode.bind(this)}
  // }

  public updateViewMode(isCollapsed,column=null) {
    if(column){
      this.setScrollTarget(isCollapsed,column);
    }
    if(this.isLoading()){
      return;
    }

    let newMode: DetailViewMode = null;
    switch (this.gridContext.viewMode) {
      case DetailViewMode.Daily:
        if (isCollapsed) {
          newMode = DetailViewMode.Weekly;
        }
        break;
      case DetailViewMode.Weekly:
        if (isCollapsed) {
          newMode = DetailViewMode.Monthly;
        } else {
          newMode = DetailViewMode.Daily;
        }
        break;
      case DetailViewMode.Monthly:
        if (!isCollapsed) {
          newMode = DetailViewMode.Weekly;
        }
        break;
    }

    this.getPlanDetail(newMode);

  }

  ngOnInit() {
    super.ngOnInit();

    this.frameworkComponents = {
      // agColumnHeaderGroup: PlanDetailHeaderRowComponent,
      // agColumnHeader: PlanDetailClickableHeaderComponent,
      planLoadingOverlay: PlanLoadingOverlayComponent,
      numericEditor: NumericEditorComponent,
      planDetailValidationCell:PlanDetailValidationCellComponent,
      muGroupHeader: MuGroupHeaderComponentV2,
      noRowsOverlay: EspNoRowsOverlayComponent,
    };

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(()=>{
      this.updateCurrentRoute();
    });

    this.route.params.subscribe(params => {
      this.planId = params["id"];
    });
    this.translationResults.subscribe((result) => {
      this.updateTranslationObj(result);
      if (this.grid && this.grid.api) {
        this.grid.api.refreshHeader();
      }
    });

    this.gridContext = {
      updateViewMode: this.updateViewMode.bind(this),
      showMessage: this.showMessage.bind(this),
      viewMode: this.gridContext.viewMode,
      isLoading: this.isLoading.bind(this),
      cellEditable: this.cellEditable.bind(this),
      setCustomValue:this.setCustomValue.bind(this),
      canDrillDown:this.canDrillDown.bind(this),
      // Option 2 inline "edit row" pencil (WCAG 2.1.1 / 4.1.2 / 4.1.3): the
      // parameter (row-header) cell renderer asks whether the row is editable
      // (to show the pencil) and starts editing the row when the pencil is
      // activated.
      isRowEditable: (node: any) => this.isRowEditable(node),
      startRowEditing: (node: any) => this.startRowEditing(node),
      isGridValid: () => this._isGridValid,
      isValidCell: (row, field) => {
        return this._invalidCells.has(this.getCellID(row)) === false;
      },
      getValidatorMessage(param) {

      },
      planSettings:()=>this.planSettings,
      entitySettings:()=>this.entitySettings,
      showNhtpDialog:(mu)=>this.showNewHireSettingsDialog(mu),
      // Full groupRowRenderer (mu-group-header-v2) no longer receives AG Grid's
      // computed valueFormatted, so resolve the human-readable MU/CT group
      // label from the loaded entity lists using the group node's key.
      getGroupDisplayName:(node)=>this.getGroupDisplayName(node),
      isRollupCollapsed:()=>this._isRollupCollapsed,
      toggleRollupGroup: ()=>{
        const value = this._isRollupCollapsed=!this._isRollupCollapsed;
        this.storeMUSelectionList();
        // WCAG 1.3.1 / 4.1.2: hide collapsed rollup data rows from the
        // accessibility tree so JAWS cannot navigate into hidden content.
        setRollupCollapsedAriaHidden(this.el.nativeElement, value);
        return value;
      },
      showNhtpLink: () => !this._newHireSettingsFeature
    };
    this.initGridOptions();
    this.initHandlers();
    this.initRowClassRules();
    this.initCellClassRules();
    this.fetchMUSelectionList();
    this.getPlanDetail(this.gridContext.viewMode);
    this.updateCurrentRoute();

    this.defaultColDef = {
      sortable: false,
      cellRenderer:"planDetailValidationCell",
      cellClassRules:this.cellClassRules,
      cellDataType: false
      // WCAG 2.1.1 Keyboard / ESP-21579: do NOT set suppressHeaderKeyboardEvent
      // for Tab here. AG Grid's own GridHeaderCtrl.onTabKeyDown already walks the
      // header cells left-to-right (Parameter -> months -> Total -> Average) via
      // navigateHorizontally, calls ensureColumnVisible so the header AND body
      // scroll together (single source of truth, no desync), and hands off into
      // the first body cell after the last header. Suppressing it handed Tab to
      // the browser, which walked the virtualized (scrambled) DOM order and
      // fake-scrolled the overflow-hidden header viewport WITHOUT moving the
      // body -- the "header moves but body doesn't" bug. The drill-down button
      // still receives focus via PlanDetailClickableHeaderComponent's focusin
      // forwarder, so Enter/Space still activate it.
    }
  }

  private initLRUCache() {
    const cachedMUs: LRUCache = new LRUCache();
    cachedMUs.set(this.planId, new CachedMU(new Array<SelectedMU>()));
    localStorage.setItem(this.cachedMUsKey, cachedMUs.asString());
    return cachedMUs;
  }

  ngAfterViewInit(): void {
    this.comboboxA11yObserver = sanitizeMultiselectComboboxValue(this.el?.nativeElement);
    this.comboboxLabelObserver = associateMultiselectVisibleLabel(this.el?.nativeElement);
    this.setupSelectorAriaControls();
  }

  /**
   * PrimeNG p-multiselect only sets aria-controls on its hidden combobox input
   * while the overlay panel is open (it binds [attr.aria-controls] reactively and
   * clears it on every change detection once closed). Assistive technologies need
   * the relationship to be present at all times, so we point the combobox at a
   * stable, visually hidden placeholder element (#ctSelector_list /
   * #muSelector_list) whenever PrimeNG has left it empty (WCAG 4.1.2). The
   * placeholders are deliberately role-less (no role="listbox") so screen readers
   * do not announce an empty "list box, 0 items"; aria-controls only needs a valid
   * IDREF, not a listbox role. The observer prefers the toolbar
   * container (to avoid reacting to AG Grid mutations) but falls back to the
   * component host so it still attaches when the toolbar/selector renders lazily
   * (e.g. behind *ngIf for nested routes or the CT/MU view toggle).
   */
  private setupSelectorAriaControls(): void {
    const host: HTMLElement = this.el.nativeElement;

    const apply = () => {
      host.querySelectorAll(
        '.ct-selector [role="combobox"], .mu-selector [role="combobox"]'
      ).forEach((node) => {
        const combobox = node as HTMLElement;
        if (!combobox.getAttribute('aria-controls')) {
          const listId = combobox.closest('.ct-selector')
            ? 'ctSelector_list'
            : 'muSelector_list';
          combobox.setAttribute('aria-controls', listId);
        }
      });
    };

    apply();
    const target: HTMLElement =
      (host.querySelector('.ctmu-container') as HTMLElement) || host;
    this.selectorAriaObserver = new MutationObserver(apply);
    this.selectorAriaObserver.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-controls'],
    });
  }

  /**
   * WCAG 2.1.1 Keyboard / 4.1.2 Name, Role, Value (Dragon voice control):
   * PrimeNG renders the CT/MU combo box on a visually hidden
   * `<input role="combobox">`. Voice-control software (Dragon) targets that
   * accessible control and issues a click on it, but PrimeNG's own
   * onContainerClick explicitly ignores clicks whose target is that hidden
   * input, so "Click 3 Email (Back Office)" never opens the panel. We detect
   * that exact case and open the overlay ourselves so the control is operable
   * by voice. Real pointer clicks land on the visible container (handled by
   * PrimeNG) and never reach this branch, so behaviour is unchanged for them.
   */
  onMultiselectContainerClick(event: MouseEvent, selector: MultiSelect | undefined): void {
    const target = event.target as HTMLElement | null;
    if (target?.getAttribute('role') === 'combobox' && selector && !selector.overlayVisible) {
      selector.show(true);
    }
  }

  /**
   * WCAG 4.1.2: the CT dropdown is functionally single-select but is built on
   * `p-multiselect`, whose overlay listbox is hardcoded `aria-multiselectable="true"`
   * with `aria-checked` on every option, so screen readers announce a multi-select.
   * On panel open we expose the CT overlay (scoped by its unique
   * `.ct-single-select-overlay` panel class) as a single-select listbox and keep it
   * corrected while options virtual-scroll; the observer is torn down on close.
   */
  onCtPanelShow(): void {
    this.ctSingleSelectAriaObserver?.disconnect();
    this.ctSingleSelectAriaObserver = enforceMultiselectSingleSelectAria(
      document.querySelector('.ct-single-select-overlay') as HTMLElement | null
    );
  }

  onCtPanelHide(): void {
    this.ctSingleSelectAriaObserver?.disconnect();
    this.ctSingleSelectAriaObserver = null;
  }

  ngOnDestroy(): void {
    this.comboboxA11yObserver?.disconnect();
    this.comboboxA11yObserver = null;
    this.comboboxLabelObserver?.disconnect();
    this.comboboxLabelObserver = null;
    this.ctSingleSelectAriaObserver?.disconnect();
    this.ctSingleSelectAriaObserver = null;
    this.selectorAriaObserver?.disconnect();
    this.selectorAriaObserver = null;
    this.fullWidthRowGroupObserver?.disconnect();
    this.fullWidthRowGroupObserver = null;
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = null;
    this.muGroupToggleObserver?.disconnect();
    this.muGroupToggleObserver = null;
    this.scrollRegionSyncCleanup?.();
    this.scrollRegionSyncCleanup = null;
    this.clearSelectionOnBlur?.();
    this.clearSelectionOnBlur = null;
    this.clearSubscriptionList();
    PlanHelper.removePlanDetailSessionId(this.planId);
    PlanHelper.removeDirtyState(this.planId);
  }

  public isLoading():boolean{
    return this._isLoading;
  }

  public noRowsComponentParams = {
    getOverlayMsg: () => {
      let msgKey = "plan.details.no.mus";
      return this.translateSrv.instant(msgKey);
    },
    image:'2'
  };
  async showMessage(titleKey, msgKey, type) {

    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = titleKey;
    dgOption.messageKey = msgKey;
    dgOption.msgType = type;
    dgOption.showCancel = false;
    dgOption.confirmLabel = "btn.ok.label";
    const dialog = modalComponent.showModalMessage(dgOption, this.modalService);
    dialog.finally(()=>{
      this.focusGrid();
    })
    return dialog;
  }

  onGridReady(params: any) {
    this.gridParams = params;
    this.gridApi = params.api;
    // Downgrade AG Grid's empty full-width rowgroup container to
    // role="presentation" (restored to rowgroup once full-width rows render).
    this.fullWidthRowGroupObserver?.disconnect();
    this.fullWidthRowGroupObserver = fixEmptyFullWidthRowGroup(this.el.nativeElement);
    // Bridge AG Grid's pinned/center row split so JAWS announces the row header
    // (parameter/metric name) alongside the column header and value for each
    // data cell (WCAG 1.3.1 / 4.1.2).
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = associateGridRowHeaders(this.el.nativeElement);
    this.columnApi = params.api;

    this.suppressLoadingOverlay=false;
    this.gridApi.hideOverlay()

    // JAWS engages table navigation (Ctrl+Alt+Arrow) only on role="grid"; AG
    // Grid defaults its root to role="treegrid". The CT view is flat, so expose
    // it as a grid; the MU view keeps treegrid for its expandable group rows.
    setGridNavigationRole(this.el?.nativeElement, this.isCTView);

    if(this._isLoading && !this.task){
      this.gridApi.showLoadingOverlay();
    }

    // WCAG 2.1.1: make scrollable grid viewport reachable by keyboard and
    // remove presentation-role elements from the tab order.
    fixGridNonActiveTabOrder(this.el.nativeElement);
    this.scrollRegionSyncCleanup?.();
    this.scrollRegionSyncCleanup = makeGridScrollRegionFocusable(this.el.nativeElement);
    // a11y (WCAG 2.4.7): drop the focused-cell highlight and range selection
    // once focus leaves the grid, so the purple cell outline does not linger
    // as a "stuck" focus indicator when nothing in the grid is focused.
    this.clearSelectionOnBlur?.();
    this.clearSelectionOnBlur = clearGridSelectionOnBlur(this.el.nativeElement, this.gridApi);
    // WCAG 2.1.1 Keyboard: Tab focus order between the MU group-row
    // expand/collapse toggle buttons.
    this.registerMuGroupToggleHandler();
  }

  /**
   * (Re)register the capture-phase Tab handler that keeps the MU group-row
   * expand/collapse toggle buttons keyboard-reachable (WCAG 2.1.1). Only the MU
   * view renders group rows, so the CT view unregisters instead. Called from
   * onGridReady AND from setEntityView: a CT<->MU switch swaps rowData on the
   * SAME grid instance (no new gridReady event), so registering only on
   * gridReady would leave the MU view without the handler whenever the page
   * opened in CT view first (ESP-18579).
   */
  private registerMuGroupToggleHandler(): void {
    this.muGroupToggleObserver?.disconnect();
    this.muGroupToggleObserver = null;
    if (this.isMUView) {
      this.muGroupToggleObserver = patchMuGroupRowToggleAria(
        this.el.nativeElement,
        (name) => this.translateSrv.instant('multi.mu.group.expand.aria', { 0: name }),
        (name) => this.translateSrv.instant('multi.mu.group.collapse.aria', { 0: name })
      );
    }
  }

  getPlanDetail(mode: DetailViewMode) {
    if(this.currentEntityView===MUCTKey.MU && this.selectedMUs.length==0){
      this.rowData = [];
      this.topRowsData = [];
      this.allRowsData = [];
      this.colDefs = [];
      return;
    }

    //If we don't have the plan details in cache, we have to trigger a background task
    this._isLoading = true;
    if(PlanHelper.isSessionExist(this.planId?.toString()) && this.selectedCTs.length > 0){
      if(this.gridApi){
        this.gridApi.showLoadingOverlay();
      }

      let payload:any = {
        planId: this.planId,
        interval: mode,
        muRollup: this.currentEntityView===MUCTKey.MU,
        planLevel:false,
      };

      if(this.currentEntityView===MUCTKey.CT && this.selectedCTs[0]?.oid===PlanDetailComponentV5.ALL_CONTACT_TYPES){
        payload.planLevel=true;
      }else if(this.currentEntityView===MUCTKey.CT){
        payload.ctOids = this.selectedCTs.map(ct=>ct.oid);
      }else if(this.currentEntityView===MUCTKey.MU){
        payload.muOids = this.selectedMUs.map(mu=>mu.oid);
      }
      this.store.dispatch(new GetPlanDetailV2(payload));
    }else{
      let payload = this.buildLoadPlanDetailPayload(mode);
      this.store.dispatch(new LoadPlanDetailV2(payload));
    }
  }

  buildLoadPlanDetailPayload(mode: any): { planId: string; interval: any; nhMigration?: boolean } {
    const payload: { planId: string; interval: any; nhMigration?: boolean } = {
      planId: this.planId,
      interval: mode
    };
    if (this._newHireSettingsFeature) {
      payload.nhMigration = true;
    }
    return payload;
  }

  initHandlers() {
    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.PlanDetailReceivedV2))
        .subscribe(this.planDetailReceiveHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.PlanNotExist))
        .subscribe(this.planNotExistHandler)
        );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.EditPlanDetailReceived))
        .subscribe(this.editPlanDetailReceiveHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.EditPlanDetailError))
        .subscribe(this.editPlanDetailErrorHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.SavePlanDetailComplete))
        .subscribe(this.savePlanDetailCompleteHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ConvertPlanFailed))
        .subscribe(this.convertPlanFailedHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.SaveAsPlanSuccessReceived))
        .subscribe(this.saveAsSuccessHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.SaveAsPlanFailed))
        .subscribe(this.saveAsFailedHandler)
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ApplyStaffingDataSuccess))
        .subscribe(this.applyStaffingDataSuccessHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ApplyMuParamsSuccess))
        .subscribe(this.applyMuParamsSuccessHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ApplyMuParamsError))
        .subscribe(this.applyMuParamsErrorHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.SavePlanSettingsSuccess))
        .subscribe(this.savePlanSettingsSuccessHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.SavePlanSettingsError))
        .subscribe(this.savePlanSettingsErrorHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ApplyFTERequiredSuccess))
        .subscribe(this.applyFTERequiredSuccessHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ApplyFTERequiredError))
        .subscribe(this.applyFTERequiredErrorHandler.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ExportEveryCtSuccess))
        .subscribe(this.exportEveryCtSuccess.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.ExportEveryCtFailed))
        .subscribe(this.exportEveryCtFailed.bind(this))
    );

    this.addToSubscriptionList(
      this.action$.pipe(ofType(AppActionTypes.TaskCreateAction,
                               AppActionTypes.TaskStartAction,
                               AppActionTypes.TaskStatusAction,
                               AppActionTypes.TaskErrorAction,
                               AppActionTypes.TaskCancelAction,
                               AppActionTypes.TaskCompleteAction)
      ).subscribe(this.taskHandler.bind(this))
    );

    this.addToSubscriptionList(
        this.action$.pipe(ofType(AppActionTypes.ApplyCTSettingsSuccess))
            .subscribe(this.applyCTSettingsSuccessHandler.bind(this))
    );

    this.addToSubscriptionList(
        this.action$.pipe(ofType(AppActionTypes.ApplyNewHireClassesSuccess))
            .subscribe(this.applyNewHireClassesSuccessHandler.bind(this))
    )

  }

  onBtnExport(): void {
    const exParam: any = {
      processHeaderCallback: this.getHeaderForCsv,
      columnGroups: true,
      allColumns: true,
      skipRowGroups: true,
      fileName: this.generateCsvFileName(),
      columnSeparator: ",",
      customHeader: this.getCsvHeader(),
      processCellCallback: this.processCell,
      shouldRowBeSkipped: this.shouldSkipRow
    };
    this.gridApi.exportDataAsCsv(exParam);
  }

  onBtnExportExcel(): void {
    const isCXOne = this.tenantService.isCXOne;
    const entityIdentifier = isCXOne ? this.getWEMskillName() : this.getCtIdStr();

    this.excelService.exportToExcel({
      gridApi: this.gridApi,
      columnApi: this.columnApi,
      planName: this.planName as string,
      username: this.username,
      viewMode: this.gridContext.viewMode.toString(),
      currentEntityView: this.currentEntityView.toString(),
      entityIdentifier: entityIdentifier,
      sheetName: 'Plan Data',
      errorToastLife: this.ERROR_TOAST_LIFE,
      processHeaderCallback: this.getHeaderForCsv,
      processCellCallback: (cell) => this.processCell(cell, !!cell.value?.label),
      shouldRowBeSkipped: this.shouldSkipRow
    });
  }

  private getCsvHeader() {
    const dateTime = new Date(Date.now()).toISOString();
    const exportedBy = this.translate.instant("plan.csv.exported.by");
    const exportDate = this.translate.instant("plan.csv.export.date");
    return `"${this.planName}",${exportedBy}: ${this.username},${exportDate}: ${dateTime}`;
  }

  onBtnImportExcel(): void {
    this.fileInput.nativeElement.click();
  }



  async onFileSelected(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    // Clear the file input to allow selecting the same file again
    event.target.value = '';

    // Use the unified Excel service
    const result = await this.excelService.importExcelFile(file, {
      gridApi: this.gridApi,
      columnApi: this.columnApi,
      gridContext: this.gridContext,
      gridOptions: this.gridOptions,
      errorToastLife: this.ERROR_TOAST_LIFE,
      currentEntityView: this.currentEntityView.toString(),
      suppressSuccessMessage: true  // We handle the message based on validation state
    });

    // Mark data as unsaved if import was successful
    if (result.success && result.updatedCells && result.updatedCells > 0) {
      this._dataIsSaved = false;

      // The import itself succeeded either way, so this stays a success toast and only
      // the wording changes when imported values need reviewing.
      //
      // The wording is decided by validating the cells the import changed, NOT by
      // reading `_isGridValid`. Nothing has updated that flag yet: ag-grid-angular is
      // handed `cellValueChanged` through AG Grid's ASYNC event queue (a setTimeout
      // hop), so `_dirtyCells` is still empty here and the 100ms `updateGridValid`
      // debounce only starts once those events land. This used to sleep 200ms and read
      // the flag; measured 2026-09-17 on the dev tenant, validation finished ~400ms
      // after the file was handed over, so the toast said "Data imported successfully"
      // over a cell the grid flagged invalid a moment later.
      this.messageService.add({
        severity: 'success',
        detail: this.translate.instant(this.areImportedCellsValid(result.changedCells)
          ? 'plan.import.success'
          : 'plan.import.success.with.validation.errors'),
        life: this.ERROR_TOAST_LIFE
      });
    }
  }

  /**
   * Whether every cell an import changed holds a valid value.
   *
   * Validating the imported cells directly keeps this independent of `_dirtyCells` -
   * which AG Grid only fills in on a later task - and of the debounced `_isGridValid`
   * that follows it. An import that changed nothing (re-importing the file the grid was
   * exported to) has nothing to validate, and is valid.
   */
  private areImportedCellsValid(changedCells: ImportedCellRef[] | undefined): boolean {
    let result = true;
    (changedCells ?? []).forEach(({node, colId}) => {
      if (!node?.data) {
        return;
      }
      const isValidCell = this.validateCellById(this.getCellID({data: node.data, colDef: {field: colId}}));
      result = result && isValidCell;
    });
    return result;
  }

  generateCsvFileName(): string {
    const properCase: any = (p: string) => { return p.charAt(0).toUpperCase() + p.substring(1).toLowerCase() };
    const viewLevel: any = this.translate.instant("plan.viewMode." + properCase(this.gridContext.viewMode.toString()));
    const muct: string = this.translate.instant(this.currentEntityView === MUCTKey.CT ? "label.ct.short" : "label.mu.short");
    //use ct name for cxone
    const isCXOne = this.tenantService.isCXOne;
    const ctIdStr = isCXOne ? this.getWEMskillName() : this.getCtIdStr();
    return `${this.planName} - ${muct} ${ctIdStr}- ${viewLevel}`;
  }

  getHeaderForCsv = (p: any) => {
    const colId = p.column.colId;
    let header = this.getStaticHeader(colId);
    return header ? header : p.column.colDef.headerNameFull;
  };

  private getCtIdStr() {
    const ctVal = this.contactTypeList.find(a => a.object !== null && a.object.oid === this.selectedCTs[0].oid);
    const id = ctVal ? ctVal.object.id : this.translate.instant("plan.details.cts.all");
    return this.currentEntityView == MUCTKey.CT ? "- " + id + " " : "";
  }

  private getWEMskillName() {
    const ctVal = this.contactTypeList.find(a => a.object !== null && a.object.oid === this.selectedCTs[0].oid);
    let ctName = ctVal ? this.getTrimmedName(ctVal.object.name) : this.translate.instant("plan.details.cts.all");

    return this.currentEntityView == MUCTKey.CT ? "- " + ctName + " " : "";

  }

  // first 50 characters of the name
  getTrimmedName(skillName: string) {
    return skillName.length > 50 ? skillName.substring(0, 50) : skillName;
  }

  setSelectedCTs(cts: any) {
      this.selectedCTs = [cts];
  }

  getStaticHeader(colId: string): string {
    if (colId === "entityInfo") return this.translate.instant("label.mu.short")
    else if (colId === "plan_label_param") return this.translate.instant("plan.label.param");
    else if (colId === "plan_label_average") return this.translate.instant("plan.label.average");
    else if (colId === "plan_label_total") return this.translate.instant("plan.label.total");
    else return null;
  }
  processCell = (p: any,format=true) => {
    const params: any = { value: p.value, data: p.node.data, colDef: p.column.colDef };
    // Closed days carry no plan data, so they export blank whether or not the value
    // is formatted. Without this the unformatted path leaks the underlying value.
    if (this.isClosedCell(params)) {
      return "";
    }
    if (format) {
      return this.getFormattedCellValues(params);
    } else {
      return params.value;
    }

  };

  private isClosedCell(params: any): boolean {
    const cellMetadata: any = params.data?.metadata?.cellmetadata?.[params.colDef.field];
    return cellMetadata?.paramStatus === PlanCellType.CLOSED;
  }

  getFormattedCellValues(params: any) {
    // code to skip new parameters because not data.
    if (!params.data || !(params.data.metadata && params.data.metadata.cellmetadata)) {
      return "";
    }

    if (this.isClosedCell(params))
      return "";
    else if (params.colDef.field === "entityInfo") {
      return params.data.entityInfo.value.label;
    }
    else {
      return this.cellValueFormatter(params);
    }
  }

  shouldSkipRow = (p: any) => p.node.data.isRollupHeader;

  onBtnExportEveryCt(): void {
    let interval = this.titleCasePipe.transform(this.gridContext.viewMode);
    const localizedInterval = this.translate.instant("plan.viewMode." + interval);
    const fileName = this.translate.instant("plan.exporteveryctcsv.filename", {0:this.planName, 1:localizedInterval});

    let payload = {
      planId: this.planId,
      interval: this.gridContext.viewMode,
      planName: this.planName,
      fileName
    };
    this._isLoading = true;
    this.gridApi.showLoadingOverlay();
    this.store.dispatch(new ExportEveryCt(payload));
  }

  exportEveryCtSuccess(action: ExportEveryCtSuccess) {
    this._isLoading = false;
    this.gridApi.hideOverlay();
  }

  exportEveryCtFailed(action: ExportEveryCtFailed) {
    this._isLoading = false;
    this.messageService.add({severity: "error", detail:this.translate.instant("unrecoverable.err.msg")});
    this.gridApi.hideOverlay();
  }

  applyNewHireClassesSuccessHandler(action: ApplyNewHireClassesSuccess){
    this._dataIsSaved = false;
  }
  applyCTSettingsSuccessHandler(action) {
    this._dataIsSaved = false;
  }

  planDetailReceiveHandler = (action: PlanDetailReceivedV2) => {
    this.localParams = PlanParam;
    this.isRenderingData=true;
    this._dirtyCells.clear();
    this._invalidCells.clear();

    setTimeout(()=>{
      if (action.payload) {

        let response:any = action.payload;
        this.isPlanLoaded=true;
        this.gridContext.viewMode=response.interval;
        this.planName = response.planName;
        this.planStartDate = response.settings.startDate;
        this.planEndDate = response.settings.endDate;
        this.planSettings = response.settings;
        this.entitySettings = {...response.entitySettings};

        this._userHasModify = false;
        if (response.settings && response.settings.userPlanPermission && response.settings.userPlanPermission === "MODIFY") {
          this._userHasModify = true;
        }

        if (PlanHelper.consumeDirtyState(this.planId)) {
          this._dataIsSaved = false;
        }


        this.planContactTypes = response.settings.planContactTypes;
        this.multiStepWorkflow = response.multiStepWorkFlow;
        this.planEntityType = response.planEntityType;
        this.entityId = response.settings.entityId;
        this.entityName = response.settings.entityName;
        this.ltForecastName = response.settings.ltForecastName;
        this.setEntitiesFromResponse(response);
        let colDefsTemp = this.getColumnsFromResponse(response);
        let rowDataTemp = this.getRowsFromResponse(response);
        this.topRowsData = this.getTopRowsFromResponse(response);
        this.colDefs = colDefsTemp;
        this.rowData = rowDataTemp;
        this.updateAllRowsData();


      } else { //server error
        if(this.planName && this.isPlanLoaded) {//we already have some grid data so stay on the grid
          this.messageService.add({severity: "error", detail: this.translate.instant("plan.details.interval.failure")});
        }else{
          this.planNotReceived();
        }
      }

      if(this.gridApi){
        // Defer hideOverlay and refreshHeader to allow AG Grid to finish
        // processing new colDefs. In v31, calling grid API methods while old
        // columns are being destroyed causes null reference errors.
        setTimeout(() => {
          this.gridApi?.hideOverlay();
          this.gridApi?.refreshHeader();
          // Re-apply aria-hidden to collapsed rollup rows after every data
          // load so the accessibility tree matches the current visual state
          // (the DOM is rebuilt on each plan-detail response).
          setRollupCollapsedAriaHidden(this.el.nativeElement, this._isRollupCollapsed);
        });
      }
      this._isLoading = false;
    },100)
  }

  planNotReceived = ()=>{
    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = this.translate.instant("plan.detail.open.failure.title");
    dgOption.messageKey = this.translate.instant("plan.detail.open.failure.msg");
    dgOption.msgType = "error";
    dgOption.showCancel = false;
    dgOption.confirmLabel = "btn.ok.label";
    var dialog = modalComponent.showModalMessage(dgOption, this.modalService);
    const navigateBack = ()=>{
      this.router.navigateByUrl("/plans");
    };
    dialog.then(navigateBack, navigateBack);
  }

  planNotExistHandler = (action: PlanNotExist) => {
    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = this.translate.instant("plan.notexist.title");
    dgOption.messageKey = this.translate.instant("plan.notexist.msg");
    dgOption.msgType = "error";
    dgOption.showCancel = false;
    dgOption.confirmLabel = "btn.ok.label";
    var dialog = modalComponent.showModalMessage(dgOption, this.modalService);
    const navigateBack = ()=>{
      this.router.navigateByUrl("/plans");
    };
    dialog.then(navigateBack, navigateBack);
  }

  editPlanDetailReceiveHandler = (action: EditPlanDetailReceived) => {
    if (action.payload) {

      this.updateExistingEntitiesRows(action);

      if(this.currentEntityView===MUCTKey.CT) {
        if (this.CTDistMode === CTDistributionCalcMethod.FTE_REQUIRED && action.payload.request.planParameterLabel != "HOURLY_RATE") {
          this.setFTERequiredDirty(true);
        }
      }

      // mu grid dirty check
      if(this.currentEntityView===MUCTKey.MU){
        this.setMUGridDirty(true);
      }

      this._dirtyCells.clear();
      this._invalidCells.clear();
      this.updateGridValid();
      // we must redraw rows since the cell-class might have changed. (Edit/Calculated)
      this.rowData=[...this.rowData];
      this.updateAllRowsData();

      this.gridApi.redrawRows();
      this._isLoading = false;
      this.gridApi.hideOverlay();
    }
    else{
      this.router.navigateByUrl("/error");
    }
  }
  updateExistingRows(updatedRows,existingRows){
    if(!updatedRows){
      return;
    }

    updatedRows.forEach((updatedRow: any) => {
      const existingRow = existingRows.find(obj => {
        return obj.metadata && obj.metadata.paramName == updatedRow.paramName;
      });
      if (existingRow) {
        Object.keys(updatedRow.data).forEach((k) => {
          const precision = Math.pow(10, updatedRow.decimal);
          let val = updatedRow.data[k];
          existingRow.metadata.cellmetadata[k] = Object.assign({}, updatedRow.metadata[k]);
          if (!existingRow.metadata.cellmetadata[k]) {
            existingRow.metadata.cellmetadata[k] = {origValue: val};
          } else {
            existingRow.metadata.cellmetadata[k].origValue = val;
          }// raw value to preserve precision
          existingRow.metadata.cellmetadata[k].workload = this.planContactTypes === PlanContactTypes.WORKLOAD;
          existingRow[k] = Number.parseFloat(val) >= 0 ? Math.round(Number(val) * precision) / precision : val;
          // existingRow[k] = Number(val)?Number(val):val;

        });

        // update total and average
        existingRow["plan_label_total"] = updatedRow.total;
        existingRow["plan_label_average"] = updatedRow.average;
      }
    });
  }
  editPlanDetailErrorHandler(action:any){
    // handle this later
    //this.router.navigateByUrl("/error");
    this._isLoading = false;
    this.messageService.add({
      severity:"error",
      detail:this.translate.instant("plan.detail.edit.failure"),
      life: this.ERROR_TOAST_LIFE
    })
    this.gridApi.hideOverlay();
  }

  savePlanDetailCompleteHandler = (action: any) => {
    this._isLoading = false;
    this.gridApi.hideOverlay();

    let errorStatus = action.payload.errorStatus;
    if (errorStatus === null) {
      this._dataIsSaved = true;
      let successMsg = this.translate.instant("plan.save.success");
      this.messageService.add({severity: "success", detail: successMsg});
    } else if (errorStatus === 409) { // plan modified
      let title = this.translate.instant("plan.save.error.title.dlg");
      let msg = this.translate.instant("plan.save.error.modified.msg");
      this.showMessage(title, msg, "error");
    } else if (errorStatus === 410) { // plan deleted
      let title = this.translate.instant("plan.save.error.title.dlg");
      let msg = this.translate.instant("plan.save.error.deleted.msg");
      this.showMessage(title, msg, "error");
    } else {
      let failureMsg = this.translate.instant("plan.save.failure");
      this.messageService.add({severity: "error", detail: failureMsg});
    }
  };

  convertPlanFailedHandler = (e: any) => {
    if (e.payload.status === 500) {
      const done = () => { };
      const dialogOpt: DialogOptions = new DialogOptions();
      dialogOpt.titleKey = "generic.err.title";
      dialogOpt.messageKey = "unrecoverable.err.msg";
      dialogOpt.msgType = "error";
      dialogOpt.confirmLabel = "btn.close";
      dialogOpt.showCancel = false;
      modalComponent.showModalMessage(dialogOpt, this.modalService).then(done, done);
      // this.messageService.add({severity: "error", detail: this.translate.instant("plan.convert.failure.general")});
    }
    else if (e.payload.status === 401) {
      // logout
      const logout = () => {
        if (this.router.navigate(["/login"]))
          this.authService.logout();
      };
      const dgOption: DialogOptions = new DialogOptions();
      dgOption.titleKey = "generic.err.title";
      dgOption.messageKey = "generic.err.msg";
      dgOption.msgType = "error";
      dgOption.confirmLabel = "btn.signin";
      dgOption.showCancel = false;
      modalComponent.showModalMessage(dgOption, this.modalService).then(logout, logout);
    }
  };

  saveAsSuccessHandler = (action: any) => {
    // set data is saved (as) so user can navigate back to managed plans page
    this._dataIsSaved = true;
    this.router.navigate(['/plans'], {queryParams: {sortBy: ColumnId.modifiedDate, sortOrder: "desc"}});
  };

  saveAsFailedHandler = async (action: any) => {
    // Toast not displayed when Save As results in a duplicate name failure and unrecoverable error
    if (![409, 500, 401].includes(action.payload.status)) {
      this.messageService.add({ severity: "error", detail: this.translate.instant("plan.save.as.failure") });
    }
  };

  protected getColumnsFromResponse(response) {

    let colDefs = [];
    if (this.translateSrv.currentLang) {
      let localeMatch = environment.locales.find(language => language.locale === this.translateSrv.currentLang);
      if (localeMatch) {
        this.dateShortFormat = localeMatch.shortDateFormat;
      }
    }



    if (response.interval === DetailViewMode.Daily) {

      // Parameters column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.param"],
        headerGroupComponent: null,
        children: [{
          pinned: "left",
          headerComponent: null,
          headerName: "",
          suppressSizeToFit: true,
          width: this.pinnedLeftColumnWidth,
          field: "plan_label_param",
          //colId: "plan_label_param",
          // ARIA grid row header (WCAG 1.3.1): expose the parameter/row-label
          // cell as role="rowheader" so JAWS announces it (e.g. "Contacts
          // Received (Forecast)") as the row's header when navigating data cells.
          cellAriaRole: "rowheader",
          tooltipField: "plan_label_param",
          // WCAG 2.1.1 Keyboard: Enter/Space on the focused parameter
          // (row-header) cell activates the inline "edit row" pencil.
          suppressKeyboardEvent: suppressRowHeaderEditButtonKeyboardEvent,
          lockPosition: true,
        }],
        headerClass: "",
        headerTooltip: this.translationStr["plan.label.param"],
        width: null
      });
      // Week/Day columns
      let weeks = response.headers;
      for (var weekKey in weeks) {
        let itemClass = "first-item";
        if (weeks.hasOwnProperty(weekKey)) {
          let children = [];
          let days = weeks[weekKey].children;
          for (let dayKey in days) {
            if (days.hasOwnProperty(dayKey)) {
              children.push({
                field: dayKey,
                //colId:dayKey,
                startDate: days[dayKey].date,
                endDate: days[dayKey].date,
                headerComponentParams: {updateViewMode: this.updateViewMode.bind(this)},
                headerComponent: null,
                headerName: this.datePipe.transform(new Date(days[dayKey].date + "T12:00:00Z"), this.dateShortFormat + "/yy"),
                headerNameFull: this.datePipe.transform(new Date(days[dayKey].date + "T12:00:00Z"), this.dateShortFormat + "/yyyy"),
                headerTooltip:this.datePipe.transform(new Date(days[dayKey].date + "T12:00:00Z"), this.dateShortFormat + "/yy"),
                suppressSizeToFit: true,
                width: this.getDateColumnWidth(),
                headerClass: "no-bold no-icon no-black " + itemClass,
                cellEditorSelector: this.cellEditor,
                editable: this.cellEditable,
                valueFormatter: this.cellValueFormatter,
                cellEditorParams: this.cellEditorParam,
                //onCellValueChanged: this.cellValueChanged,
                validator: ValidatorFactory.getValidator,
                valueSetter: this.cellValueSetter.bind(this),

                lockPosition:true,
              });
            }
            itemClass = "";
          }
          colDefs.push({
            headerName: this.datePipe.transform(new Date(weeks[weekKey].startDate + "T12:00:00Z"), this.dateShortFormat) +
                  "-" + this.datePipe.transform(new Date(weeks[weekKey].endDate + "T12:00:00Z"), this.dateShortFormat),
            headerNameFull: this.datePipe.transform(new Date(weeks[weekKey].startDate + "T12:00:00Z"), this.dateShortFormat+ "/yy") +
                  "-" + this.datePipe.transform(new Date(weeks[weekKey].endDate + "T12:00:00Z"), this.dateShortFormat+ "/yy"),
            headerGroupComponent: PlanDetailHeaderRowComponent,
            // ESP-21579: no suppressHeaderKeyboardEvent for Tab. AG Grid's native
            // header navigation walks the group header cells and keeps header +
            // body scroll in sync via ensureColumnVisible; the collapse button is
            // reached through PlanDetailHeaderRowComponent's focusin forwarder, so
            // Enter/Space still collapse/expand.
            children: children,
            headerClass: "",
            width: null,
            lockPosition:true,
          });
        }
        itemClass = "";
      }

      // Total column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.total"],
        headerGroupComponent: null,
        children: [{
          pinned: "right", headerComponent: null,
          headerName: "",
          suppressSizeToFit: true,
          width: this.pinnedRightTotalColumnWidth,
          field: "plan_label_total",
          //colId: "plan_label_total",
          tooltip: this.cellValueFormatter,
          valueFormatter: this.cellValueFormatter,
          suppressMovable: true
        }],
        headerClass: "black text-right",
        headerTooltip: this.translationStr["plan.label.total"],
        width: null
      });

      // Average column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.average"],
        headerGroupComponent: null,
        children: [{
          pinned: "right", headerComponent: null, suppressSizeToFit: true,
          width: this.pinnedRightColumnWidth,
          field: "plan_label_average",
          //colId: "plan_label_average",
          tooltip: this.cellValueFormatter,
          valueFormatter: this.cellValueFormatter,
          headerName: "",

          suppressMovable: true
        }],
        headerClass: "black text-right",
        headerTooltip: this.translationStr["plan.label.average"],
        width: null
      });
    }

    if (response.interval === DetailViewMode.Weekly) {

      // Parameters column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.param"],
        headerGroupComponent: null,
        children: [{
          pinned: "left",
          headerComponent: null,
          headerName: "",
          suppressSizeToFit: true,
          width: this.pinnedLeftColumnWidth,
          field: "plan_label_param",
          //colId: "plan_label_param",
          // ARIA grid row header (WCAG 1.3.1): expose the parameter/row-label
          // cell as role="rowheader" so JAWS announces it (e.g. "Contacts
          // Received (Forecast)") as the row's header when navigating data cells.
          cellAriaRole: "rowheader",
          tooltipField: "plan_label_param",
          // WCAG 2.1.1 Keyboard: Enter/Space on the focused parameter
          // (row-header) cell activates the inline "edit row" pencil.
          suppressKeyboardEvent: suppressRowHeaderEditButtonKeyboardEvent,
          lockPosition: true
        }],
        headerClass: "",
        headerTooltip: this.translationStr["plan.label.param"],
        width: null,
      });

      // Month/Week columns
      let months = response.headers;
      for (var monthKey in months) {
        let itemClass = "first-item";
        if (months.hasOwnProperty(monthKey)) {
          let children = [];
          let weeks = months[monthKey].children;
          for (var weekKey in weeks) {
            if (weeks.hasOwnProperty(weekKey)) {
              if (weeks[weekKey].partialFlag) {
                itemClass = itemClass + " partial-field";
              }
              children.push({
                field: weekKey,
                //colId: weekKey,
                startDate: weeks[weekKey].startDate,
                endDate: weeks[weekKey].endDate,
                headerComponentParams: {updateViewMode: this.updateViewMode.bind(this)},
                headerComponent: this.currentEntityView===MUCTKey.MU?null: PlanDetailClickableHeaderComponent,
                headerName: this.datePipe.transform(new Date(weeks[weekKey].startDate + "T12:00:00Z"), this.dateShortFormat) +
                  "-" + this.datePipe.transform(new Date(weeks[weekKey].endDate + "T12:00:00Z"), this.dateShortFormat),
                headerNameFull: this.datePipe.transform(new Date(weeks[weekKey].startDate + "T12:00:00Z"), this.dateShortFormat+ "/yyyy") +
                  "-" + this.datePipe.transform(new Date(weeks[weekKey].endDate + "T12:00:00Z"), this.dateShortFormat+ "/yyyy"),
                // Native browser tooltip for the week subheader. The CT view uses
                // PlanDetailClickableHeaderComponent (its own <span title>), but the
                // MU view falls back to AG Grid's default header (headerComponent:
                // null) which only shows a tooltip when headerTooltip is set, so add
                // it here to keep the week tooltip consistent across CT and MU views.
                headerTooltip: this.datePipe.transform(new Date(weeks[weekKey].startDate + "T12:00:00Z"), this.dateShortFormat) +
                  "-" + this.datePipe.transform(new Date(weeks[weekKey].endDate + "T12:00:00Z"), this.dateShortFormat),
                suppressSizeToFit: true,
                width: this.getDateColumnWidth(),
                headerClass: "no-bold no-icon no-black " + itemClass,
                cellEditorSelector: this.cellEditor,
                editable: this.cellEditable,
                valueFormatter: this.cellValueFormatter,
                cellEditorParams: this.cellEditorParam,
                //onCellValueChanged: this.cellValueChanged,
                validator: ValidatorFactory.getValidator,
                valueSetter: this.cellValueSetter.bind(this),

                lockPosition:true,
              });
            }
            itemClass = "";
          }
          colDefs.push({
            headerName: format(new Date(months[monthKey].startDate + "T12:00:00Z"), "MMM yyyy", {locale: this.dateLocale}),
            headerNameFull: format(new Date(months[monthKey].startDate + "T12:00:00Z"), "MMM yyyy", {locale: this.dateLocale}),
            headerGroupComponent: PlanDetailHeaderRowComponent,
            // ESP-21579: no suppressHeaderKeyboardEvent for Tab. AG Grid's native
            // header navigation walks the group header cells and keeps header +
            // body scroll in sync via ensureColumnVisible; the collapse button is
            // reached through PlanDetailHeaderRowComponent's focusin forwarder, so
            // Enter/Space still collapse/expand.
            children: children,
            headerClass: "",
            width: null,
            lockPosition:true,
          });
        }
        itemClass = "";
      }

      // Total column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.total"],
        headerGroupComponent: null,
        children: [{
          pinned: "right", headerComponent: null,
          headerName: "",
          suppressSizeToFit: true,
          width: this.pinnedRightTotalColumnWidth,
          field: "plan_label_total",
          //colId: "plan_label_total",
          tooltip: this.cellValueFormatter,
          valueFormatter: this.cellValueFormatter,

          suppressMovable: true
        }],
        headerClass: "black text-right",
        headerTooltip: this.translationStr["plan.label.total"],
        width: this.pinnedRightTotalColumnWidth
      });

      // Average column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.average"],
        headerGroupComponent: null,
        children: [{
          pinned: "right", headerComponent: null, suppressSizeToFit: true,
          width: this.pinnedRightColumnWidth,
          field: "plan_label_average",
          //colId: "plan_label_average",
          tooltip: this.cellValueFormatter,
          valueFormatter: this.cellValueFormatter,
          headerName: "",

          suppressMovable: true
        }],
        headerClass: "black text-right",
        headerTooltip: this.translationStr["plan.label.average"],
        width: this.pinnedRightColumnWidth
      });
    }

    if (response.interval === DetailViewMode.Monthly) {
      // Parameters column
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.param"],
        pinned: "left",
        field: "plan_label_param",
        //colId: "plan_label_param",
        // ARIA grid row header (WCAG 1.3.1): expose the parameter/row-label
        // cell as role="rowheader" so JAWS announces it (e.g. "Contacts
        // Received (Forecast)") as the row's header when navigating data cells.
        cellAriaRole: "rowheader",
        tooltipField: "plan_label_param",
        // WCAG 2.1.1 Keyboard: Enter/Space on the focused parameter
        // (row-header) cell activates the inline "edit row" pencil.
        suppressKeyboardEvent: suppressRowHeaderEditButtonKeyboardEvent,
        width: this.pinnedLeftColumnWidth,
        suppressMovable: true,
        // lockPosition keeps this ahead of the month columns in
        // getAllGridColumns() — AG Grid v31 moves lockPosition columns to the
        // front, so without it the CSV export (allColumns) emits this after
        // the months, just before Total/Average.
        lockPosition: true,
        headerTooltip: this.translationStr["plan.label.param"]
      });
      let columns = response.headers;
      for (var columnKey in columns) {
        if (columns.hasOwnProperty(columnKey)) {

          let startDateStr = columns[columnKey].startDate;
          var startDate = new Date(startDateStr + "T12:00:00Z");
          let lastDayOfMon = this.lastday(startDate.getFullYear(), startDate.getMonth());
          let endDateStr = format(lastDayOfMon, "yyyy-MM-dd", {locale: this.dateLocale});
          let colDef: any = {
            field: columnKey,
            //colId: columnKey,
            startDate: startDateStr,
            endDate: endDateStr,
            headerComponentParams: {updateViewMode: this.updateViewMode.bind(this)},
            headerComponent: PlanDetailClickableHeaderComponent,
            headerName: format(startDate, "MMM yyyy", {locale: this.dateLocale}),
            headerNameFull: format(startDate, "MMM yyyy", {locale: this.dateLocale}),
            suppressSizeToFit: true,
            width: this.getDateColumnWidth(),
            lockPosition:true
          };
          colDef.cellEditorSelector = this.cellEditor;
          colDef.editable = this.cellEditable.bind(this);
          colDef.valueFormatter = this.cellValueFormatter;
          colDef.cellEditorParams = this.cellEditorParam;
          //colDef.onCellValueChanged = this.cellValueChanged;
          colDef.validator = ValidatorFactory.getValidator;
          colDef.valueSetter = this.cellValueSetter.bind(this);
          if (columns[columnKey].partialFlag) {
            colDef.headerClass = "partial-field"
          }
          colDefs.push(colDef);
        }
      }
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.total"],
        pinned: "right",
        field: "plan_label_total",
        //colId: "plan_label_total",
        tooltip: this.cellValueFormatter,
        headerClass: "black text-right",
        width: this.pinnedRightTotalColumnWidth,
        valueFormatter: this.cellValueFormatter,
        suppressMovable: true,
        headerTooltip: this.translationStr["plan.label.total"]
      });
      colDefs.push({
        headerName: null,
        headerValueGetter: () => this.translationStr["plan.label.average"],
        pinned: "right",
        field: "plan_label_average",
        //colId: "plan_label_average",
        tooltip: this.cellValueFormatter,
        valueFormatter: this.cellValueFormatter,
        headerClass: "black text-right",
        width: this.pinnedRightColumnWidth,
        suppressMovable: true,
        headerTooltip: this.translationStr["plan.label.average"]
      });
    }

    if(this.currentEntityView===MUCTKey.MU){
      colDefs.unshift({
        headerName: null,
        pinned:'left',
        headerValueGetter: () => "Entity",
        field:"entityInfo",
        keyCreator: params => {
          const entityInfo = params.data.entityInfo;
        const key = params.value?.type + "-" + entityInfo?.value.oid;
        return  key;
        },
        valueGetter: params => {
        return params.data.entityInfo?.value;
        },
        valueFormatter: param => {
          const mu = this.managementUnitsList.find(({value}) => "MU-"+value.oid == param.value);
         return mu?.value.label;
        },
        rowGroup: true, hide: true,
        // Same v31 export-order issue as plan_label_param: without
        // lockPosition this column sorts after the locked date columns in
        // getAllGridColumns(), misplacing it in the CSV export.
        lockPosition: true
      })
    }

    return colDefs;

  }

  /**
   * Resolves the human-readable group label for an MU/CT group row rendered by
   * the full `mu-group-header-v2` groupRowRenderer (which does not get AG Grid's
   * computed `valueFormatted`). The group node's key is `<type>-<oid>` (e.g.
   * `MU-2c99...`), matching the keyCreator on the entity rowGroup column.
   */
  public getGroupDisplayName(node: any): string {
    const key = node?.key;
    if (!key) {
      return "";
    }
    const mu = this.managementUnitsList?.find(
      ({ value }) => "MU-" + value.oid === key
    );
    if (mu) {
      return mu.value.label;
    }
    const ct = this.contactTypeList?.find(
      (c) => c?.object && "CT-" + c.object.oid === key
    );
    if (ct) {
      return ct.object.label ?? ct.label ?? key;
    }
    return node?.groupData?.[node?.field] ?? key;
  }

  public lastday = (y, m) => {
    return new Date(y, m + 1, 0);
  };

  public floor(num: any, dec: any) {
    if (num && num.toString().includes(".")) {
      var array = num.toString().split(".");
      var decPart = array.pop();
      if (!decPart) {
        decPart = "0";
      }
      array.push(decPart.substring(0, dec));
      var result = array.join(".");
      return result;
    } else {
      return num;
    }
  }

  public isPartial(params) {
    var field = params.colDef.field;
    let cellMeta = params.data?.metadata !== undefined ? params.data.metadata.cellmetadata[field] : null;
    return cellMeta && cellMeta.partialFlag;
  }

  public hasVariance(params) {
    var field = params.colDef.field;
    let cellMeta = params.data?.metadata.cellmetadata[field];
    return cellMeta && cellMeta.varianceFlag;
  }

  public setVarianceOff(params) {
    if (this.hasVariance(params)) {
      var field = params.colDef.field;
      let cellMeta = params.data?.metadata.cellmetadata[field];
      cellMeta.varianceFlag = false;
    }
  }

  cellValueSetter(params) {
    var decimal = params.data?.metadata.decimal;
    var newVal:any = params.newValue ? parseFloat(this.floor(params.newValue, decimal)) : parseFloat(params.newValue);
    var oldVal:any = params.oldValue ? parseFloat(this.floor(params.oldValue, decimal)) : parseFloat(params.oldValue);
    oldVal = isNaN(oldVal) ? "" : oldVal;

    var data = params.data;
    var result = false;

    if (newVal !== oldVal) {
      newVal = isNaN(newVal) ? "" : newVal
      if (this.hasVariance(params) && this.isManualEditing) {
        data[params.colDef.field] = newVal;
        this.confirmAndUpdate(params, oldVal);
      } else {
        data[params.colDef.field] = newVal;
        result = true;
      }

    } else {
      result = false;
    }
    return result;

  }

  confirmAndUpdate(params, oldVal) {
    let planParam: PlanParam = <PlanParam>PlanParam[params.data.metadata.paramName];
    //Parent parameter will set the warning message
    if(params.data?.metadata.parent){
      planParam = <PlanParam>PlanParam[params.data.metadata.parent];
    }
    let confMsg = ConfirmationMsgFactory.getMsg({
      paramName: planParam,
      viewMode: this.gridContext.viewMode
    });

    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = confMsg.title;
    dgOption.messageKey = confMsg.msg;
    dgOption.msgType = "warn";
    const dialog = modalComponent.showModalMessage(dgOption, this.modalService);
    dialog.then((yes) => {
      this.setVarianceOff(params);
      var refreshParam = {
        rowNodes: [params.node],
        columns: [params.column]
      };
      this.gridApi.refreshCells(refreshParam);
      this.cellValueChanged(params);
      this.focusGrid();
    }, (no) => {
      params.data[params.colDef.field] = oldVal;
      var refreshParam = {
        rowNodes: [params.node],
        columns: [params.column]
      };
      this.gridApi.refreshCells(refreshParam);
      this.rowData=[...this.rowData];//We must refresh the grid because the chart needs to revert as well
      this.updateAllRowsData();
      this.updateGridValid();
      this.focusGrid();
    });
  }

  public cellValueFormatter = (params: any) => {
    if (params && params.data && params.data.metadata) {
      let options = {decimal: params.data.metadata.decimal};
      return NumberUtils.formatNumberIfNecessary(params.value, this.locale, options.decimal);
    }
  };


  public saveButtonClicked = () => {
    this.savePlan();
  };

  private savePlan(){
    this._isLoading = true;
    this.gridApi.showLoadingOverlay();
    this.store.dispatch(new SavePlanDetail(this.planId));
  }


  public cellEditable = (params: any) => {
    let editable = false;
    let paramName: PlanParam = null;

    if(params.colDef) { //charts doesn't have col definition
      var field = params.colDef.field;
      let cellMetadata = null;
      if (params.data && params.data.metadata && params.data.metadata.cellmetadata) {
        cellMetadata = params.data.metadata.cellmetadata[field];
      }
      if (!params.data || !params.data.metadata || !params.data.metadata.paramName
        || (cellMetadata && cellMetadata.paramStatus === PlanCellType.CLOSED)) {
        return false;
      }
    }
    editable = params.data.metadata.permission===ParamPermission.Modify;

    if(this._isGridValid===false && this._dirtyCells.has(this.getCellID(params))==false){
      editable=false;
    }
    return editable;
  };

  /**
   * The first data column (in display order) whose cell can be edited for the
   * given row, or null when the row has no editable cell. Used by the inline
   * "edit row" pencil to know where to start editing.
   */
  private getFirstEditableColumn(rowNode: any): any {
    if (!this.gridApi || !rowNode?.data) {
      return null;
    }
    const columns = this.gridApi.getAllDisplayedColumns?.() ?? this.gridApi.getColumns?.() ?? [];
    for (const column of columns) {
      const colDef = column.getColDef();
      // Only value columns carry an `editable` config; the label/param columns
      // do not, so they are skipped.
      if (!colDef?.editable) {
        continue;
      }
      const cellParams = {
        colDef,
        data: rowNode.data,
        node: rowNode,
        column,
        rowIndex: rowNode.rowIndex,
        api: this.gridApi
      };
      if (this.cellEditable(cellParams)) {
        return column;
      }
    }
    return null;
  }

  /**
   * Whether the given row currently has at least one editable cell, so the
   * inline "edit row" pencil should be shown for it (WCAG 4.1.2).
   */
  public isRowEditable(rowNode: any): boolean {
    return this.getFirstEditableColumn(rowNode) !== null;
  }

  /**
   * Starts editing a row from its inline "edit row" pencil: moves editing to
   * the first editable cell of the row and announces the edit-mode instructions
   * so screen-reader users know how to move between cells and cancel (WCAG
   * 4.1.3 Status Messages).
   */
  public startRowEditing(rowNode: any): void {
    const column = this.getFirstEditableColumn(rowNode);
    if (!column || rowNode?.rowIndex == null) {
      return;
    }
    this.gridApi.startEditingCell({ rowIndex: rowNode.rowIndex, colKey: column.getColId() });
    const rowLabel = rowNode.data?.plan_label_param ?? "";
    announceToScreenReader(
      this.el?.nativeElement,
      this.translateSrv.instant("plan.detail.edit.row.editing.announce", { row: rowLabel })
    );
  }

  public cellEditor = (params: any) => {
    let paramName: PlanParam = <PlanParam>PlanParam[params.data.metadata.paramName];

    if(!paramName && params.data.metadata.parent){
      paramName = <PlanParam>PlanParam[params.data.metadata.parent + "_SUB"];
    }

    var editor = {component: null, param: params};
    switch (paramName) {
      case PlanParam.ACTUAL_FTE:
      case PlanParam.SHRINKAGE:
      case PlanParam.SHRINKAGE_SUB:
      case PlanParam.ASA:
      case PlanParam.MS_EFFICIENCY:
      case PlanParam.CONTACTS:
      case PlanParam.HOURLY_RATE:
      case PlanParam.OCCUPANCY:
      case PlanParam.NEW_HIRE_FTE:
      case PlanParam.AHT:
      case PlanParam.BACKLOG:
      case PlanParam.SERVICE_LEVEL_PERCENT:
      case PlanParam.SERVICE_LEVEL_TIME:
      case PlanParam.ABS_ATTRITION:
      case PlanParam.MEAN_CONCURRENCY:
        editor.component = "numericEditor";
        break;

      default:
        break;
    }
    return editor;
  };

  public cellEditorParam = (params: any) => {
    let paramName: PlanParam = <PlanParam>PlanParam[params.data.metadata.paramName];
    const param = {
      origValue: params.data[params.colDef.field],
      metadata: params.data.metadata,
      incremental: true,
      locale: this.locale
    };
    //gets added to the editor parameter
    return param;
  };

  setFakeIdForCXOneMU(muInfoResponseData: any) {
    let muInfo:any = [...muInfoResponseData].sort((mu1,mu2)=>{
      if(mu1.name.toLowerCase() < mu2.name.toLowerCase()) {
        return -1;
      } else if(mu1.name.toLowerCase() > mu2.name.toLowerCase()) {
        return 1;
      }
      return 0;
    }).map((item: any, index: number) => {
      const newItem = { ...item };
        newItem.id = index;
      return newItem;
    });
    return muInfo;
  }

  private setEntitiesFromResponse(response: PlanDetailResponseV2) {
    const isCXone = this.tenantService.isCXOne;

    if(this.contactTypeList.length>0 || this.managementUnitsList.length>0){
      // If in the MU view, update the data model so the currently selected MU(s) is displayed
      // in the input area
      if (this.isMUView && this.muSelector) {
        this.muSelector.selectedOptions = [...this.cacheSelectedMUs];
        this.muSelector.updateModel(this.muSelector.selectedOptions);
        this.muSelector.onModelChange(this.muSelector.value);
        this.muSelector.onChange.emit({ originalEvent: null, value: this.muSelector.value });
      }
      return;
    }

    const getSelectItems = (obj, type) => {
      let item = {
        type,
        styleClass: "",
        disabled: false,
        value: {
          oid:obj.oid,
          label:"",
          type
        },
        object: obj
      };
      if (type == MUCTKey.CT) {
        item.value.label = isCXone ? obj.name : obj.id + " " + obj.name;
      } else if (type == MUCTKey.MU) {
        item.value.label = isCXone ? obj.name : obj.id + " " + obj.name;
      }
      return item;
    }

    let muInfo:any = response.muInfo;
    //set fake id to muInfo for CXOne plan for MU chart use
    if (isCXone) {
      muInfo = this.setFakeIdForCXOneMU(muInfo);
    }

    const muItems: Array<any> = muInfo.map((mu) => getSelectItems(mu, MUCTKey.MU));
    const ctItems: Array<any> = response.ctInfo.map((ct) => getSelectItems(ct, MUCTKey.CT));


    let firstItem = null;
    if(this.multiStepWorkflow){
      let initialOid = this.getInitialCtOid();
      let initialCt = ctItems.find((item)=>item.object.oid==initialOid);
      let initialCtIndex = ctItems.indexOf(initialCt);
      ctItems.splice(initialCtIndex,1);
      firstItem = initialCt;
    }else{
      firstItem = {
        value: {
          oid:PlanDetailComponentV5.ALL_CONTACT_TYPES,
          label: this.translate.instant("plan.details.cts.all"),
          type: "ALL"
        },
        object: null,
        disabled: this._isMUDirty
      };
    }

    if (response.planEntityType != EntityType.CT || this.multiStepWorkflow) {
      ctItems.unshift(firstItem);
    } else {
      firstItem = ctItems[0];
    }

    if(ctItems.length>1){
      ctItems[0].separator = true
    }

    if (this.selectedCTs.length===0){
      this.selectedCTs = [firstItem.value];
    }

    // if(this.selectedMUs.length === 0) {
    if(this.cacheSelectedMUs.length===0){
      this.fetchMUSelectionList();
    }

    this.restoreMuSelection(muItems);

    this.contactTypeList = ctItems;
    this.managementUnitsList = muItems;

    const selectedMUids = [];
    for(const mu of this.selectedMUs){
      selectedMUids.push(mu.oid);
    }
    this.managementUnitsList.forEach((mu) => {
      const selected = selectedMUids.indexOf(mu.value.oid) > -1;
      mu.isSelected = selected;
    });
  }
  protected getEntityInfo(oid){
    if(this.currentEntityView == MUCTKey.MU){
      return this.managementUnitsList.filter((mu)=>mu.value.oid == oid)[0]
    }else{
      return this.contactTypeList.filter((ct)=>ct.value.oid == oid)[0];
    }
  }
  protected restoreMuSelection(muItems){

    if(this.cacheSelectedMUs.length===0 && muItems.length>0){
      this.selectedMUs = [muItems[0].value];
      muItems[0].isSelected = true;
      // Copying to the array object so it can be written to local storage shortly thereafter
      this.cacheSelectedMUs = [...this.selectedMUs];
      this.storeMUSelectionList();
    } else {
      this.selectedMUs = [...this.cacheSelectedMUs];

    }
  }
  protected getRowsFromResponse(response) {

    let rowGroup = 1;
    let rows = [];

    const sortedArray = this.sortMUs(Object.keys(response.entityParams));
    sortedArray.forEach((entityOid,index)=>{
        Object.keys(this.localParams).map(paramKey => {
          let remoteParam = response.entityParams[entityOid].find(obj => obj.paramName == paramKey);
          let entityInfo = this.getEntityInfo(entityOid);
          let row = this.getRow(remoteParam, paramKey, rowGroup, entityInfo);
          if(remoteParam){
            //Modify the FTE NEW HIRE row to be read only if new hire feature if ON
            if (this._newHireSettingsFeature === true && PlanParam[paramKey] === PlanParam.NEW_HIRE_FTE && this.currentEntityView === MUCTKey.MU) {
              row["metadata"].permission = 'VIEW';
            }

            // Determine if row should be included based on new hire feature and parameter type
            const shouldIncludeRow = this.shouldIncludeRowBasedOnNewHireFeature(paramKey);
            if (shouldIncludeRow) {
              rows.push(row);
            }
          }

          rowGroup = Math.abs(rowGroup - 1);

          let remoteParamChildren = response.entityParams[entityOid].filter(obj => obj.parent == paramKey);
          if (remoteParamChildren.length > 0) {
            remoteParamChildren.forEach((remoteChild)=>{
              let childRow = this.getRow(remoteChild, remoteChild.paramName, rowGroup, entityInfo);
              rows.push(childRow);
            });
            rowGroup = Math.abs(rowGroup - 1);
          }

        });
    });
    return rows;
  }

  isNewHireMUParameters(paramKey: string) : boolean{
    const newHireParams = [PlanParam.FTE_ACTUAL_NEW_HIRE, PlanParam.ACTUAL_FTE_TOTAL, PlanParam.FTE_TOTAL];
    return this.localParams && this.localParams[paramKey] && newHireParams.includes(PlanParam[paramKey]);
  }

  isNewHireCTParameters(paramKey: string) : boolean{
    const newHireParams = [PlanParam.AHT_FORECAST, PlanParam.FTE_TOTAL, PlanParam.ACTUAL_FTE_TOTAL, PlanParam.AHT_NEW_HIRE];
    return this.localParams && this.localParams[paramKey] && newHireParams.includes(PlanParam[paramKey]);
  }

  /**
   * Determines if a row should be included based on the new hire feature flag and parameter type
   * @param paramKey - The parameter key to check
   * @returns true if the row should be included, false otherwise
   */
  private shouldIncludeRowBasedOnNewHireFeature(paramKey: string): boolean {
    const isMUNewHireParam = this.isNewHireMUParameters(paramKey);
    const isCTNewHireParam = this.isNewHireCTParameters(paramKey);
    const isMUView = this.currentEntityView === MUCTKey.MU;
    const isCTView = this.currentEntityView === MUCTKey.CT;

    // If the parameter is not a new hire parameter, always include it
    if ((!isMUNewHireParam && isMUView) || (!isCTNewHireParam && isCTView)) {
      return true;
    } else if (this._newHireSettingsFeature === true) {
      // When new hire feature is enabled, include new hire parameters only for the relevant view
      return (isMUNewHireParam && isMUView) || (isCTNewHireParam && isCTView);
    } else {
      // When new hire feature is disabled, exclude all new hire parameters
      return false;
    }
  }

  sortMUs (array) {

    return array.sort((a, b) => {
      const sortKeyObjA = this.getMuObject(a);
      const sortKeyObjB = this.getMuObject(b);

      // Compare the numeric parts first
      if (sortKeyObjA.id !== sortKeyObjB.id) {
        return sortKeyObjA.id - sortKeyObjB.id;
      }

      // If numeric parts are equal, compare the string parts
      return sortKeyObjA.name.localeCompare(sortKeyObjB.name,undefined, { sensitivity: 'base' });
    });
  }

  protected getRow(remoteParam, paramKey, rowGroup, entityInfo){
    let rowTemp = {};

    //if it's a static parameter, we have a translation for it
    if(PlanParam[paramKey]){
      rowTemp["plan_label_param"] = this.getParameterTranslationKey(paramKey);
    }

    if (remoteParam && remoteParam.data) {

      let paramName: PlanParam = <PlanParam>PlanParam[paramKey];

      if(!paramName && remoteParam.parent){
        paramName = <PlanParam>PlanParam[remoteParam.parent + "_SUB"];
      }
      let validator = ValidatorFactory.getValidator(paramName, remoteParam, this.translate, this.locale);

      // Apply new hire feature specific translations and validation messages
      this.applyNewHireTranslations(paramKey, rowTemp, validator, remoteParam);

      const rowMetaData: any = {decimal: remoteParam.decimal, paramName: paramKey, permission: remoteParam.permission, validator: validator, cellmetadata: new Map<String, any>(),};

      const precision = Math.pow(10, remoteParam.decimal);


      if (remoteParam.metadata) {
        for (let [key, value] of Object.entries(remoteParam.metadata)) {
          rowMetaData.cellmetadata[key]=Object.assign({},value);
          rowMetaData.cellmetadata[key]["minValue"] = remoteParam.minValue;
          rowMetaData.cellmetadata[key]["maxValue"] = remoteParam.maxValue;
        }
      }
      const options = this.getOptionsFromRow(remoteParam);
      for (const cellKey in remoteParam.data) {
        if (remoteParam.data.hasOwnProperty(cellKey)) {
          const val = remoteParam.data[cellKey];
          if (rowMetaData.cellmetadata[cellKey]) {
            rowMetaData.cellmetadata[cellKey].origValue = val;
          } else {
            rowMetaData.cellmetadata[cellKey]= {origValue:val};
          }// raw value to preserve precision
          rowMetaData.cellmetadata[cellKey].workload = this.planContactTypes === PlanContactTypes.WORKLOAD;
          //rowTemp[cellKey] = Number(val)!==NaN ? Math.round(Number(val) * precision) / precision : val;//this.formatNumberIfNecessary(remoteParam.data[cellKey],paramKey,options);
          rowTemp[cellKey] = Math.abs(Number.parseFloat(val)) >= 0 ? Math.round(Number(val) * precision) / precision : val;

        }
      }
      rowTemp["metadata"] = rowMetaData;
    }
    rowTemp["entityInfo"] = entityInfo;
    rowTemp["plan_label_total"] = remoteParam && remoteParam.total ? remoteParam.total : "";
    rowTemp["plan_label_average"] = remoteParam && remoteParam.average ? remoteParam.average : "";
    rowTemp["rowGroup"] = rowGroup;

    //If we're a child row
    if(remoteParam && remoteParam.parent){
      rowTemp["plan_label_param"] = remoteParam.label;
      rowTemp["rowChild"] = true;
      rowTemp["metadata"].parent = remoteParam.parent;
    }

    return rowTemp;

  }

  /**
   * Gets the appropriate translation key for a parameter based on the new hire feature flag
   * @param paramKey - The parameter key to get the translation for
   * @returns The translated parameter label
   */
  private getParameterTranslationKey(paramKey: string): string {
    const planParam = PlanParam[paramKey];

    // Special handling for CONTACTS parameter
    if (planParam === PlanParam.CONTACTS) {
      return this.translate.instant(`${planParam}.forecast`);
    }

    // Special handling for AHT parameter based on new hire feature flag
    if (planParam === PlanParam.AHT) {
      const suffix = this._newHireSettingsFeature ? 'productive' : 'forecast';
      return this.translate.instant(`${planParam}.${suffix}`);
    }

    // Default translation for all other parameters
    return this.translate.instant(planParam);
  }

  /**
   * Applies new hire feature specific translations and validation messages
   * @param paramKey - The parameter key
   * @param rowTemp - The row object to update
   * @param validator - The validator object to update
   * @param remoteParam - The remote parameter data
   */
  private applyNewHireTranslations(paramKey: string, rowTemp: any, validator: any, remoteParam: any): void {
    if (!this._newHireSettingsFeature) {
      return;
    }

    const planParam = PlanParam[paramKey];

    // Handle FTE Actual translation and validation for MU view
    if (planParam === PlanParam.ACTUAL_FTE && this.currentEntityView === MUCTKey.MU) {
      rowTemp["plan_label_param"] = this.translate.instant("plan.detail.fte.act.productive");
      validator.msg = this.formatParamRangeValidationErrorMsg(remoteParam, "validation.err.actual_fte_productive", this.translate, this.locale);
    }

    // Handle AHT translation and validation for CT view
    if (planParam === PlanParam.AHT && this.currentEntityView === MUCTKey.CT) {
      rowTemp["plan_label_param"] = this.translate.instant("plan.detail.aht.productive");
      validator.msg = this.formatParamRangeValidationErrorMsg(remoteParam, "validation.err.aht.productive", this.translate, this.locale);
    }
  }

  private formatParamRangeValidationErrorMsg(remoteParam, translationKey, translate, locale) {
    let min:string;
    let max:string;
    min = ValidatorFactory.formattedValue(remoteParam.minValue, locale, 2);
    max = ValidatorFactory.formattedValue(remoteParam.maxValue, locale, 2);
    return translate.instant(translationKey, {0: min, 1: max});
  }

  protected getOptionsFromRow(row) {
    var options = {
      decimal: null
    };
    if ("decimal" in row) {
      options.decimal = row.decimal;
    }
    return options;
  }


  protected  getSelectedEntityOid(){
    if(this.currentEntityView==MUCTKey.CT){
      if(this.selectedCTs.length===0 || this.selectedCTs[0].oid === PlanDetailComponentV5.ALL_CONTACT_TYPES){
        return null;
      }else{
        return this.selectedCTs[0].oid;
      }
    }else{
      return this.selectedMUs[0].oid;
    }

  }

  protected addToSubscriptionList(newSubscription: Subscription) {
    this.subscriptionList.push(newSubscription);
  }

  private clearSubscriptionList() {
    if (this.subscriptionList.length > 0) {
      this.subscriptionList.forEach(subscriptionItem => subscriptionItem.unsubscribe());
      this.subscriptionList = null;
    }
  }

  toggleChartButtonClicked(numOfCharts) {
    this.isChartView=numOfCharts>0;
    if(this.isChartView===true){
      this.chartsCount=numOfCharts;
    }
  }

  public setCustomValue(row,field, value){
    var rowNode:IRowNode<any> = null;
    this.gridApi.forEachLeafNode((tableRow)=> {
      if(tableRow.data.entityInfo.value.oid === row.entityInfo.value.oid && tableRow.data.plan_label_param===row.plan_label_param){
        rowNode = tableRow;
      }
    });

    let columns = this.columnApi.getColumns();
    if (!columns || !rowNode) return;
    //chart is sending the field name, but ag grid generates a unique column id.
    //so we must find the right column id
    let column = columns.find((col)=>col.getColDef()?.field==field);
    if (!column) return;
    rowNode.setDataValue(column.getColId(), value);

  }
  private shouldSave():boolean{
    return !this.authService.isTokenExpired() && !this._dataIsSaved && this._userHasModify;
  }

  async canDeactivate() {
    if(!this.shouldSave()){
      return true;
    }

    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = "plan.exit.unsaved.dialog.title";
    dgOption.messageKey = "plan.exit.unsaved.dialog.msg";
    dgOption.msgType = "warn";
    dgOption.showCancel = true;
    dgOption.confirmLabel = "btn.confirm.label";
    try{
      await modalComponent.showModalMessage(dgOption, this.modalService);
      return true;
    }catch(e){
      return false;
    }
  }
  @HostListener("window:beforeunload", ["$event"])
  canCloseTab($event: any) {
    PlanHelper.setDirtyState(this.planId, this.shouldSave());
    if(this.shouldSave()){
      $event.returnValue=true;
    }
  }
  @HostListener("window:unload", ["$event"])
  tabClosed($event: any){
    PlanHelper.removePlanDetailSessionId(this.planId);
  }

  private updateCurrentRoute() {

    if(this.route.snapshot.firstChild){
      this._isNestedRoute=true;
    }else{
      if(this._isNestedRoute==true){ //if we came from nested route, we should refresh data
        this.fetchMUSelectionList(); //New hire page can update the mu selection
        this.restoreMuSelection([]);
        this.getPlanDetail(this.gridContext.viewMode);
      }
      this._isNestedRoute=false;
    }
  }

  private navigateToDistribution(){
    this.router.navigate(["distribution"],{relativeTo: this.route, state: {saved: this._dataIsSaved}});
  }

  public canDrillDown() {
    if(this.gridContext.viewMode==DetailViewMode.Daily){
      return false;
    }else if(this.currentEntityView==MUCTKey.MU && this.gridContext.viewMode==DetailViewMode.Weekly){
      return false;
    }else{
      return true;
    }
  }
  private taskHandler(taskAction:any) {
    if (taskAction.type==AppActionTypes.TaskCompleteAction) {
      this.task = null;
    } else if(taskAction.type==AppActionTypes.TaskCancelAction) {
      this.task = null;
    }else if(taskAction.type==AppActionTypes.TaskErrorAction){
      this.task = null;
    } else { //progress
      this.task = taskAction.payload;
      if(this.task.metadata && this.task.metadata.planName){
        this.planName=this.task.metadata.planName
      }
    }
  }

  private applyStaffingDataSuccessHandler() {
    this._dataIsSaved=false;
  }

  private applyMuParamsSuccessHandler() {
    this._isLoading = false;
    this.gridApi.hideOverlay();
    this.setMUGridDirty(false);
    let successMsg = this.translate.instant("plan.detail.apply.mu.edit.success");
    this.messageService.add({severity: "success", detail: successMsg});
  }

  private applyMuParamsErrorHandler() {
    this._isLoading = false;
    this.gridApi.hideOverlay();
    let errorMsg = this.translate.instant("plan.detail.apply.mu.edit.failure");
    this.messageService.add({severity: "error", detail: errorMsg});
  }

  private savePlanSettingsSuccessHandler(){
    this._dataIsSaved = false;
    let successMsg = this.translate.instant("plan.settings.applied.successfully");
    this.messageService.add({severity: "success", detail: successMsg});
    this.getPlanDetail(this.gridContext.viewMode);
    this.setFTERequiredDirty(false);
  }
  private async savePlanSettingsErrorHandler(e){
    let payload = e.payload;
    let error = payload.error;
    let planSettings = {...payload.planSettings};

    if(error.EXCEPTION && error.EXCEPTION.Recoverable===false){
      let title = this.translate.instant("plan.detail.apply.settings.critical.title");
      let msg = this.translate.instant("plan.detail.apply.settings.critical.failure");
      try{
        await this.showMessage(title, msg, "error");
      }finally{
        this.router.navigateByUrl("/plans");
      }
    }else{
      let errorMsg = this.translate.instant("plan.detail.apply.settings.failure");
      this.messageService.add({severity: "error", detail: errorMsg});
      this._showPlanSettingsDialog(planSettings, true);
    }
    console.log(e);
  }
  private applyFTERequiredSuccessHandler(){
    this.setFTERequiredDirty(false);
    this._dataIsSaved=false;
    let successMsg = this.translate.instant("plan.detail.apply.fte.required.success");
    this.messageService.add({severity: "success", detail: successMsg});
    this.getPlanDetail(this.gridContext.viewMode);
  }
  private applyFTERequiredErrorHandler(e){
    let errorMsg = this.translate.instant("plan.detail.edit.failure");
    this.messageService.add({
      severity: "error",
      detail: errorMsg,
      life: this.ERROR_TOAST_LIFE
    });
  }
  private setFTERequiredDirty(dirty:boolean){
    this._isFTERequiredDirty=dirty;

    this.contactTypeList = this.contactTypeList.map((ctItem)=>{
      if(dirty && ctItem.type===MUCTKey.MU){
        ctItem.disabled=true;
      }else{
        ctItem.disabled=false;
      }
      return ctItem;
    });
  }

  onGridColumnsChanged($event: ModelUpdatedEvent) {
    // Column changes (drill-down / roll-up / view-mode switch) do NOT rebuild
    // the AG Grid viewport DOM (ag-body-viewport, ag-center-cols-viewport,
    // tab guards). The a11y fixes applied in onGridReady therefore remain valid
    // across column reloads and do not need to be re-applied here.
    this.scrollToTargetColumn();
  }

  private setScrollTarget(isCollapsed, column) {
    let field = column.colDef.field;
    let parts = field.split("_");

    this.scrollTargetColumn = parts[0];

    if (isCollapsed && this.gridContext.viewMode === DetailViewMode.Weekly) {
      this.scrollTargetColumn = this.scrollTargetColumn.substring(0, 6);
    }
    const scrollContainer = document.querySelector('.ag-body-horizontal-scroll-viewport');
    const scrollPosition = scrollContainer ? scrollContainer.scrollLeft : 0;
    this.scrollTargetLeft = column.left - scrollPosition;
  }

  private scrollToTargetColumn() {
    let targetColumn = null;
    let columns = [];

    //if we only switched CT/MU we won't have a target column
    if (!this.scrollTargetColumn) {
      this.isRenderingData = false;
      return;
    }

    //flat all columns
    this.colDefs.forEach((column) => {
      if (column.children) {
        columns.push(...column.children);
      } else {
        columns.push(column);
      }
    });


    targetColumn = columns.find((column) => column.field.startsWith(this.scrollTargetColumn));
    this.scrollTargetColumn = null;

    if (!targetColumn) {
      this.isRenderingData = false;
      return;
    }

    let column = this.columnApi.getColumn(targetColumn.field);
    let scrollLeft = column.getLeft() - this.scrollTargetLeft;
    if (scrollLeft < 0) {
      scrollLeft = 0;
    }

    const scrollViewport = document.querySelector('.ag-body-horizontal-scroll-viewport') as HTMLElement;
    if (scrollViewport) {
      scrollViewport.scrollLeft = scrollLeft;
    }

    //adding some timeout to allow the scrollbar to relocate
    setTimeout(() => {
      this.isRenderingData = false;
    }, 100);
  }

  private getInitialCtOid() {
    let initialCT = this.multiStepWorkflow.cts.find((ct)=>ct.initial);
    if(initialCT){
      return initialCT.ctOid;
    }else{
      return null;
    }
  }
  initGridOptions() {
    this.excelStyles = this.excelService.getExcelStyles();

    this.gridOptions = {
      // A single click should only SELECT the cell (showing the range-selection
      // border and the fill handle / bottom-right square), not open the editor.
      // Editing still starts on double-click, Enter, or by typing a printable
      // key while the cell is focused. Keep singleClickEdit disabled so the
      // click-to-select spreadsheet behaviour is preserved.
      singleClickEdit: false,
      onCellEditingStarted: this.cellEditingStarted.bind(this),
      onCellEditingStopped: this.cellEditingStopped.bind(this),
      onCellValueChanged: this.cellValueChanged.bind(this),
      onPasteStart: this.onPasteStart.bind(this),
      onPasteEnd: this.onPasteEnd.bind(this),
      //processCellForClipboard:this.processCellForClipboard.bind(this),
      sendToClipboard: this.sendToClipboard.bind(this),
      processDataFromClipboard: this.processDataFromClipboard.bind(this),
      processCellFromClipboard: this.processCellFromClipboard.bind(this),
      groupDisplayType: 'groupRows',
      groupDefaultExpanded: 1,
      suppressGroupRowsSticky: true,
      isFullWidthRow: this.isFullWidthRow.bind(this),
      fullWidthCellRenderer: MuGroupHeaderComponentV2,
      excelStyles: this.excelStyles,
      defaultExcelExportParams: {
        excelStyles: this.excelStyles
      }
    }
  }
  cellEditingStarted(params: any) {
    this.editingContext = params;
  }

  cellEditingStopped(params: any) {

  }

  cellValueChanged(cellInfo) {
    this._dataIsSaved=false;
    this._dirtyCells.add(this.getCellID(cellInfo));
    this._isPristine = false;
    this.updateGridValid();
  }

  onPasteStart(){

  }
  onPasteEnd() {
    this.announceClipboardAction("plan.grid.clipboard.pasted");
  }

  /**
   * AG Grid delegates the actual clipboard write to this callback when a range
   * is copied (Ctrl+C). We write the grid's own tab-separated string verbatim
   * (identical to AG Grid's default), then announce "Copied to clipboard" so
   * screen reader users get confirmation (WCAG 4.1.3 Status Messages).
   */
  async sendToClipboard(params: { data: string }) {
    const data = params?.data ?? "";
    try {
      await clipboardCopy(data);
      this.announceClipboardAction("plan.grid.clipboard.copied");
    } catch {
      // Intentionally no success announcement on failure.
    }
  }

  /** Speak a clipboard status message through the shared aria-live region. */
  private announceClipboardAction(messageKey: string) {
    const message = this.translateSrv.instant(messageKey);
    announceToScreenReader(this.el?.nativeElement, message);
  }

  processDataFromClipboard(params) {
    let data = params.data;
    let lastRow = data[data.length - 1];
    if (data.length > 1 && lastRow.length === 1 && lastRow[0] === "") { //ms excel adds a new row with empty cell on windows
      data.pop();                           //which we need to remove before the cell parsing
    }
    data = this.duplicateClipboardToSelection(data)
    return data;
  }

  processCellFromClipboard(params) {
    const val = params.value;
    const num = NumberUtils.parseLocaleNumber(val, this.locale);
    return isNaN(num) ? val : num;
  }

  getCellID(cellInfo) {
    if(!cellInfo.data){
      return "";
    }

    let id = cellInfo.data.entityInfo?.value.oid + "|" + cellInfo.data.metadata.paramName + "|" + cellInfo.colDef.field;
    return id;
  }

  updateGridValid() {
    let result = true;
    this._dirtyCells.forEach((cellId: string) => {
      let isValidCell = this.validateCellById(cellId);
      result = result && isValidCell;
    });

    this._isGridValid = result;
    if(result && this._dirtyCells.size>1){
      this.dispatchMultiEditRequest();
    }else if(result && this._dirtyCells.size===1){
      this.dispatchEditRequest();
    }

    this.refreshCells();
  }
  validateCellById(cellId: string) {
    let [oid, param, date] = cellId.split("|");
    let row = this.rowData.find((row) => row.entityInfo.value.oid===oid && row.metadata?.paramName===param);
    if(!row){
      return false;
    }
    let value = row[date];
    const cellMetadata = row.metadata.cellmetadata?.[date];
    let validator = row.metadata.validator;//ValidatorFactory.getValidator(PlanParam[param]);
    if(!validator){
      // No validator means we cannot assess this cell (e.g. a paste landed on a
      // cell whose metadata was never initialised). Treat it as valid rather than
      // throwing, which would abort the paste and leave the grid in a broken state.
      return true;
    }
    let isValid = validator.isValid(value,cellMetadata);
    if (isValid) {
      this._invalidCells.delete(cellId);
    } else {
      this._invalidCells.add(cellId);
    }

    return isValid;
  }

  private refreshCells() {
    if (this.gridApi) {
      this.gridApi.refreshCells({force: true});
    }
  }

  private getColumnDefByField(field) {
    for (const column of this.colDefs) {
      if (column.field === field) {
        return column;
      }

      if (column.children) {
        const childColumn = column.children.find(child => child.field === field);
        if (childColumn) {
          return childColumn;
        }
      }
    }
    return null;
  }

  private getEditData(cellId){
    let [oid, param, date] = cellId.split("|");

    let row = this.rowData.find((row) => row.entityInfo.value.oid===oid && row.metadata?.paramName===param);
    if(!row){
      return null;
    }
    let column = this.getColumnDefByField(date)
    if(!column){
      return null;
    }
    var decimal = row.metadata.decimal;
    let value = row[date] !== ""  ? Number(row[date]).toFixed(decimal) : row[date];

    const cellMetadata = row.metadata.cellmetadata?.[date];

    return {
      oid,
      editedValue:value,
      startDate:column.startDate,
      endDate:column.endDate,
      parameter:param,
      previousValue:cellMetadata?.origValue,
    }
  }
  private dispatchMultiEditRequest() {
    this._dataIsSaved = false;
    let request: MultiEditPlanDetailRequestDTOV2 = {
      planId: this.planId,
      editInterval: this.gridContext.viewMode,
      editedData: {},
      editType: this.getEditType(),
      muRollup: this.currentEntityView===MUCTKey.MU
    }
    this._dirtyCells.forEach((dirtyCell) => this.setEditedData(request, dirtyCell));
    this.store.dispatch(new MultiEditPlanDetailV2(request));
  }

  private setEditedData(editRequest: MultiEditPlanDetailRequestDTOV2, dirtyCell:any){
    const editData = this.getEditData(dirtyCell);
    if(!editData){
      return;
    }
    let {oid, editedValue, startDate, endDate, parameter, previousValue} = editData;

    if (!editRequest.editedData[oid]) {
      editRequest.editedData[oid] = {};
    }
    if(!editRequest.editedData[oid][startDate]){
      editRequest.editedData[oid][startDate] = {
        startDate,
        endDate,
        parameters: {}
      }
    }

    editRequest.editedData[oid][startDate].parameters[parameter] = {
      previousValue,
      editedValue
    }
  }

  private dispatchEditRequest() {
    let [dirtyCell] = this._dirtyCells;
    const editData = this.getEditData(dirtyCell);
    if(!editData){
      return;
    }
    let {oid, editedValue, startDate, endDate, parameter, previousValue} = editData;

    this._isLoading = true;
    this._dataIsSaved = false;
    this.gridApi.showLoadingOverlay();
    let payload: EditPlanDetailRequestDTO = {
      planId: this.planId,
      startDate: startDate,
      endDate: endDate,
      interval: this.gridContext.viewMode,
      planParameterLabel: parameter,
      editedValue: editedValue,
      previousValue: previousValue,
      ctOid: this.currentEntityView == MUCTKey.CT ? oid == PlanDetailComponentV5.ALL_CONTACT_TYPES ? null : oid : null,
      muOid: this.currentEntityView == MUCTKey.MU ?  oid: null,
      muRollup: this.currentEntityView == MUCTKey.MU
    };
    this.store.dispatch(new EditPlanDetail(payload));

  }

  private initCellClassRules(){
    this.cellClassRules = {

      // Add cell class for all non-header cells for Excel export
      "cell": (params) => {
        return params.colDef.field !== "plan_label_average" &&
          params.colDef.field !== "plan_label_total" &&
          params.colDef.field !== "plan_label_param";
      },
      "invalid": (params) => {
        return this._isGridValid == false && this._invalidCells.has(this.getCellID(params));
      },
      "closed-field":(params)=>{
        if(!params.data?.metadata) return false;
        let cellMetadata = params.data.metadata.cellmetadata[params.colDef.field];
        if (cellMetadata && cellMetadata.paramStatus === PlanCellType.CLOSED) {
          return true;
        }
      },
      "locked": (params) => {
        if(params.colDef.field == "plan_label_average" ||
           params.colDef.field == "plan_label_total" ||
          params.colDef.field==="plan_label_param" ){
          return false;
        }
        return this._isGridValid == false && this._dirtyCells.has(this.getCellID(params)) == false;
      },
      "calculated-field":(params)=>{
        return this.isCalculatedField(params)
      },
      "capped-field":(params)=>{
        return this.isCappedField(params)
      },
      "cell-editable":(params)=>{
        return this.cellEditable(params);
      },
      "cell-readonly":(params)=>{
        return !this.cellEditable(params);
      },
      "partial-field":(params)=>{
        return this.isPartial(params);
      },
      "negative-value":(params)=>{
        if(!params.data?.metadata) return false;
        let cellMetadata = params.data.metadata.cellmetadata[params.colDef.field];
        let cellValue = (cellMetadata && cellMetadata.origValue) ? cellMetadata.origValue : params.value;
        return NumberUtils.isNegativeNumber(cellValue);
      },
      "question-marks":(params)=>{
        if(!params.data?.metadata) return false;
        let cellMetadata = params.data.metadata.cellmetadata[params.colDef.field];
        let cellValue = (cellMetadata && cellMetadata.origValue) ? cellMetadata.origValue : params.value;
        return cellValue==="???";
      }
    }
  }
  initRowClassRules() {
    this.rowClassRules = {
      "editable-row":(params)=>params.data?.metadata?.permission==="MODIFY",
      "readonly-row":(params)=>params.data?.metadata?.permission==="VIEW"
    }
  }
  isCalculatedField(params){
    if(params.colDef.field == "plan_label_average" || params.colDef.field == "plan_label_total" ){
      return true;
    }

    if(!params.data?.metadata) return false;
    let cellMetadata = params.data.metadata.cellmetadata[params.colDef.field];
    return cellMetadata && cellMetadata.paramStatus === PlanCellType.CALCULATED
  }

  isCappedField(params){
    if(!params.data?.metadata) return false;
    let cellMetadata = params.data.metadata.cellmetadata[params.colDef.field];
    return cellMetadata && cellMetadata.paramStatus === PlanCellType.CAPPED
  }
  focusGrid(){
    let focusedCell = this.gridApi.getFocusedCell();
    if(focusedCell){
      this.gridApi.setFocusedCell(focusedCell.rowIndex,focusedCell.column);
    }
  }

  onCellEditingStarted() {
    this.isManualEditing=true;
  }

  onCellEditingStopped() {
    this.isManualEditing=false;
  }

  discard() {
    this.getPlanDetail(this.gridContext.viewMode);
    this._dirtyCells.clear();
    this._invalidCells.clear();
    this.updateGridValid();
  }

  //this method is a workaround for a bugfix with Ag-Grid.
  //Ag-Grid doesn't handle a case where you have several ranges which
  //are larger than the copied selection
  public duplicateClipboardToSelection(data: any) {
    let cellRanges = this.gridApi.getCellRanges();

    //if (cellRanges.length == 1) {
    //  return data;
    //}

    let maxDistance = cellRanges.reduce((max, cellRange) => {
      return Math.max(cellRange.endRow.rowIndex - cellRange.startRow.rowIndex, max);
    }, 0);

    while (data.length <= maxDistance) {
      data = [...data, ...data];
    }

    return data;
  }

  private getTopRowsFromResponse(response: any) {
    const grpRollupHeader = this.translate.instant("multi.mu.rollup.group.header");
    const entityInfo = {
      disabled: false,
      label: grpRollupHeader,
      object: null,
      styleClass: null,
      type: null,
      isRollup:true,
      value: {label:grpRollupHeader}
    };
    if(this.currentEntityView!==MUCTKey.MU) {
      return null;
    }
    let params = response.muRollupParams;
    let rows:any = [
      {...this.getRow(params[0],"Rollup Header",1, entityInfo),isRollupHeader:true}
    ];
    if(params){
      params.forEach((remoteParam)=>{
        if (this.shouldIncludeRollupParameter(remoteParam.paramName)) {
          rows.push(this.getRow(remoteParam, remoteParam.paramName, 1, entityInfo));
        }
      })
    }
    return rows;
  }

  /**
   * Determines whether a rollup parameter should be included in the top rows
   * @param paramName - The parameter name to evaluate
   * @returns true if the parameter should be included, false otherwise
   */
  private shouldIncludeRollupParameter(paramName: string): boolean {
    // When new hire feature is enabled, exclude ACTUAL_FTE
    if (this._newHireSettingsFeature === true) {
      return paramName !== "ACTUAL_FTE";
    }

    // When new hire feature is disabled, exclude ACTUAL_FTE_TOTAL and FTE_TOTAL
    return paramName !== "ACTUAL_FTE_TOTAL" && paramName !== "FTE_TOTAL";
  }

  private isFullWidthRow(params){
    if(params.rowNode.data?.isRollupHeader===true)
      return true;
    return false;
  }

  updateSelectedCTs($event: any) {
    this.ctSelector.updateModel([$event.itemValue.value]);
    this.ctSelector.hide();
    this.getPlanDetail(this.gridContext.viewMode);
  }

  updateSelectedMUs($event: any) {
    const selectedMUids = this.selectedMUs.map(mu => mu.oid);
    let selectedItems = [];
    this.managementUnitsList.forEach((mu) => {
      const clicked = $event.value.indexOf(mu.value) > -1;
      const previous = selectedMUids.includes(mu.value.oid);
      mu.isSelected = clicked || previous;
      if(mu.isSelected) selectedItems.push(mu.value);
    });
    this.muSelector.updateModel(selectedItems);
    this.storeMUSelectionList();
    this.shouldRefreshMUs = true;
  }

  setEntityView(view:string) {
    if(this.currentEntityView===view){
      return;
    }

    if(this.currentEntityView===MUCTKey.CT && this.gridContext.viewMode == DetailViewMode.Daily) {
        this.gridContext.viewMode = DetailViewMode.Weekly;
    }

    this.currentEntityView = MUCTKey[view];
    // Keep the grid's ARIA navigation role in step with the view: flat CT view
    // uses role="grid" (JAWS table navigation), MU view keeps role="treegrid".
    setGridNavigationRole(this.el?.nativeElement, this.isCTView);
    this.registerMuGroupToggleHandler();
    this.getPlanDetail(this.gridContext.viewMode);
  }

  public fetchMUSelectionList() {
    let lruCacheStr = localStorage.getItem(this.cachedMUsKey);
    if (!lruCacheStr) {
      this.initLRUCache();
    } else {
      const {selectedMUList, rollupExpanded} = this.getSelectedMUsFromCache(lruCacheStr);

      //set selected MUs and group widget state
      this.cacheSelectedMUs = selectedMUList.map((selectedMU: SelectedMU) => {
        this.muStateMap.set(selectedMU.oid, selectedMU.isExpanded);
        return selectedMU.mu;
      });

      // set rollup state
      if (this._isRollupCollapsed === rollupExpanded) {
        this.gridContext.toggleRollupGroup();
      }
    }
  }

  muStateMap = new Map();

  public getSelectedMUsFromCache(lruCacheStr: string) {
    const lruCache: LRUCache = new LRUCache();
    lruCache.init(lruCacheStr);
    let cachedMUState: CachedMU = lruCache.get(this.planId);

    //plan opened for first time (not in cache)
    if (!cachedMUState || !cachedMUState.selectedMUList) {
      cachedMUState = new CachedMU(new Array<SelectedMU>());
      lruCache.set(this.planId, cachedMUState);
    }
    return cachedMUState;
  }

  public rowGroupOpened(a:any){
    const oid = this.selectedMUs[a.node.childIndex].oid;
    this.muStateMap.set(oid, a.expanded);
    this.storeMUSelectionList();
  }

  public isGroupOpenByDefault = params => {
    if (params.key.startsWith("MU-")) {
      const muOid = params.key.substring(3);
      const expanded = this.muStateMap.get(muOid);
      return expanded;
    }
      return false;
  };


  public storeMUSelectionList(){
    let cacheStr = localStorage.getItem(this.cachedMUsKey);
    const cache:LRUCache = new LRUCache();
    if (!cacheStr) {
      cache.set(this.planId, new CachedMU(new Array<SelectedMU>()));
    } else {
      cache.init(cacheStr);
    }

    // console.log(cacheStr, this.muStateMap, this.selectedMUs);
    let selectedMUList;
    if (this.isMUView) {
      selectedMUList = this.selectedMUs.map(mu => {
        return new SelectedMU(mu.oid, mu, this.muStateMap.get(mu.oid))
      });
    } else {
      selectedMUList = this.cacheSelectedMUs.map(mu => {
        return new SelectedMU(mu.oid, mu, this.muStateMap.get(mu.oid))
      });
    }

    const cachedMUs = new CachedMU(new Array<SelectedMU>());
    cachedMUs.selectedMUList = selectedMUList;
    cachedMUs.rollupExpanded = !this._isRollupCollapsed;
    cache.set(this.planId, cachedMUs);
    localStorage.setItem(this.cachedMUsKey, cache.asString());
    this.fetchMUSelectionList();
  }

  public clearSelectedMUs(){
    this.selectedMUs = [];
  }

  muSelectorClosed() {
    if(this.shouldRefreshMUs){
      this.getPlanDetail(this.gridContext.viewMode);
      this.shouldRefreshMUs = false;
    }
  }
  getMuID(oid){
    return this.managementUnitsList.find((mu)=>mu.object.oid===oid).object.id;
  }

  getMuObject(oid){
    return this.managementUnitsList.find((mu)=>mu.object.oid===oid).object;
  }
  getEditType(){
    if(this.currentEntityView == MUCTKey.MU){
      return MUCTKey.MU;
    }else if(this.currentEntityView == MUCTKey.CT){
      if(this.selectedCTs[0]?.oid===PlanDetailComponentV5.ALL_CONTACT_TYPES){
        return "ALL";
      }else{
        return MUCTKey.CT;
      }
    }
  }

  private updateExistingEntitiesRows(response:EditPlanDetailReceived) {
    const { muRollupParams, editValues, editedEntities, request } = response.payload;

    if(muRollupParams){ //Mu Rollup
      this.updateExistingRows(response.payload.muRollupParams,this.topRowsData);
    }
    if(editValues){ //Single cell editing.
        let entityOid = this.getEntityOidFromRequest(request);
        let entityRows = this.rowData.filter((row)=>row.entityInfo.value.oid===entityOid);
        this.updateExistingRows(response.payload.editValues,entityRows);
    }
    if(editedEntities){ //When doing copy/paste we have a dictionary of entities
      let editedEntities = response.payload.editedEntities;
      Object.keys(editedEntities).forEach((oid)=>{
        let editedEntityParams = editedEntities[oid];
        let entityRows = this.rowData.filter((row)=>row.entityInfo.value.oid===oid);
        this.updateExistingRows(editedEntityParams,entityRows);
      })
    }
  }

  private getEntityOidFromRequest(request) {
    return request.ctOid || request.muOid || request.entityOid || PlanDetailComponentV5.ALL_CONTACT_TYPES;
  }

  private updateAllRowsData() {
    if(this.topRowsData){
      this.allRowsData = [...this.topRowsData,...this.rowData];
    }else{
      this.allRowsData = [...this.rowData];
    }

  }

  navigateToPlanCtSettings() {
    this.router.navigate(["ct-settings"], {
      relativeTo: this.route,
      state: {
        ct: this.getSelectedCtID(),
        saved: this._dataIsSaved
      }
    });
  }

  navigateToNewHireSettings() {
    this.router.navigate(["new-hire"], {
      relativeTo: this.route,
      state: {
        ct: this.getSelectedCtID(),
        entityView: this.currentEntityView,
        saved: this._dataIsSaved
      }
    });
  }

  navigateToExternalForecast() {
    this.router.navigate(["external-forecast"], {relativeTo: this.route});
  }

  getPlanSettingsText() {
      return this.translateSrv.instant("plan.settings.full");
  }
  getSelectedCtID(){
    if(this.currentEntityView==MUCTKey.CT){
      return this.selectedCTs[0].oid;
    }
    return null;
  }


}

export enum MUCTKey {
  MU = "MU", CT = "CT"
}

